import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import { toast } from 'react-toastify';
import { PlusIcon, PencilIcon, TrashIcon, ArrowPathIcon, CheckIcon, XMarkIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, PhotoIcon, AdjustmentsHorizontalIcon, ArrowUpTrayIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

// Componente SearchableSelect personalizzato
const SearchableSelect = ({ options, value, onChange, placeholder, name }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  
  // Aggiorna le opzioni filtrate quando cambiano le opzioni o il termine di ricerca
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      setFilteredOptions(
        options.filter(option => 
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [options, searchTerm]);
  
  // Trova l'etichetta corrente in base al valore
  const currentLabel = options.find(option => option.value === value)?.label || placeholder;
  
  return (
    <div className="relative w-full">
      <div 
        className="w-full border border-gray-300 rounded p-2 bg-white cursor-pointer flex justify-between items-center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`${value ? 'text-gray-900' : 'text-gray-500'}`}>
          {currentLabel}
        </span>
        <svg className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>
      
      {isOpen && (
        <div className="absolute z-[200] w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
          <div className="p-2 border-b">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca..."
              className="w-full p-1 border border-gray-300 rounded"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="p-2 text-gray-500 text-center">Nessun risultato</div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`p-2 cursor-pointer hover:bg-gray-100 ${value === option.value ? 'bg-blue-50 text-blue-700' : ''}`}
                  onClick={() => {
                    onChange({ target: { name, value: option.value } });
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Componente per il modale di selezione dei servizi
const ServiziModal = ({ isOpen, onClose, servizi = [], serviziSelezionati = [], onChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [localSelezionati, setLocalSelezionati] = useState([...serviziSelezionati]);
  
  useEffect(() => {
    setLocalSelezionati([...serviziSelezionati]);
  }, [serviziSelezionati, isOpen]);
  
  // Filtra i servizi in base al termine di ricerca
  const filteredServizi = searchTerm 
    ? servizi.filter(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
    : servizi;
  
  // Gestisce il toggle di tutti i servizi
  const handleToggleAll = () => {
    if (localSelezionati.length === servizi.length) {
      setLocalSelezionati([]);
    } else {
      setLocalSelezionati([...servizi]);
    }
  };
  
  // Gestisce il toggle di un singolo servizio
  const handleToggle = (servizio) => {
    if (localSelezionati.includes(servizio)) {
      setLocalSelezionati(prev => prev.filter(s => s !== servizio));
    } else {
      setLocalSelezionati(prev => [...prev, servizio]);
    }
  };
  
  // Applica le modifiche e chiude il modale
  const handleApply = () => {
    onChange(localSelezionati);
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
          <h3 className="text-lg font-medium text-gray-900">Seleziona Servizi</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        
        {/* Barra di ricerca e seleziona tutto */}
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center mb-3">
            <div className="relative flex-grow">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca servizi..."
                className="w-full px-3 py-2 pl-8 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 transform -translate-y-1/2" />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="seleziona-tutti-modal"
                checked={localSelezionati.length === servizi.length && servizi.length > 0}
                onChange={handleToggleAll}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="seleziona-tutti-modal" className="ml-2 text-sm text-gray-700 font-medium">
                Seleziona tutti
              </label>
            </div>
            <span className="text-sm text-gray-500">
              {localSelezionati.length} di {servizi.length} selezionati
            </span>
          </div>
        </div>
        
        {/* Lista dei servizi (scrollabile) */}
        <div className="p-4 overflow-y-auto flex-grow">
          {filteredServizi.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">
              Nessun servizio trovato
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {filteredServizi.map((servizio) => (
                <div 
                  key={servizio} 
                  className={`flex items-center p-2 rounded hover:bg-gray-50 ${
                    localSelezionati.includes(servizio) ? 'bg-blue-50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    id={`service-modal-${servizio}`}
                    checked={localSelezionati.includes(servizio)}
                    onChange={() => handleToggle(servizio)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label 
                    htmlFor={`service-modal-${servizio}`} 
                    className="ml-2 text-sm text-gray-700 cursor-pointer flex-grow"
                  >
                    {servizio}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer con pulsanti */}
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-lg">
          <button 
            onClick={onClose} 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Annulla
          </button>
          <button 
            onClick={handleApply} 
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Applica
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente per la selezione dei servizi (trigger del modale)
const ServiziSelector = ({ servizi, serviziSelezionati, onChange }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Formatta la visualizzazione dei servizi selezionati
  const renderServiziSummary = () => {
    if (!serviziSelezionati || serviziSelezionati.length === 0) {
      return <span className="text-gray-500 italic">Nessun servizio selezionato</span>;
    }
    
    const count = serviziSelezionati.length;
    const total = servizi.length;
    
    if (count === total) {
      return <span className="text-green-600 font-medium">Tutti i servizi selezionati</span>;
    }
    
    return (
      <div className="space-y-1">
        <div className="flex items-center">
          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full mr-2">
            {count}/{total}
          </span>
          <span className="text-sm font-medium">{serviziSelezionati[0]}</span>
        </div>
        
        {count > 1 && (
          <div className="text-xs text-gray-500 ml-1">
            {serviziSelezionati.slice(1, 3).join(', ')}
            {count > 3 && ' ...'}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="w-full flex justify-between items-center px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm text-left focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 hover:bg-gray-50"
      >
        <div className="flex-grow truncate">
          {renderServiziSummary()}
        </div>
        <PencilIcon className="h-4 w-4 text-gray-400" />
      </button>
      
      <ServiziModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        servizi={servizi}
        serviziSelezionati={serviziSelezionati}
        onChange={onChange}
      />
    </div>
  );
};

const OfferteVettureList = () => {
  // Ref per l'input file del caricamento offerte
  const fileInputRef = useRef(null);
  
  // Leggi i parametri URL
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [offerte, setOfferte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [vetture, setVetture] = useState([]);
  const [vettureFiltered, setVettureFiltered] = useState([]);
  const [societa, setSocieta] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [selectedVetturaId, setSelectedVetturaId] = useState('');
  const [newRow, setNewRow] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [selectedVettura, setSelectedVettura] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10
  });
  const [vettureOfferteCount, setVettureOfferteCount] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMarca, setFilterMarca] = useState('');
  const [marche, setMarche] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loadingVetture, setLoadingVetture] = useState(false);
  const [showVehicleIds, setShowVehicleIds] = useState(false);
  const [showOfferIds, setShowOfferIds] = useState(false);
  
  // **NUOVO STATO PER IL FILTRO VETTURE CON OFFERTE**
  const [filterVettureConOfferte, setFilterVettureConOfferte] = useState(false);
  
  // Reset paginazione quando cambiano i filtri
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  }, [searchQuery, filterMarca, filterVettureConOfferte]);
  
  // Stati per la scadenza delle offerte
  const [scadenzaOfferte, setScadenzaOfferte] = useState(null);
  const [showScadenzaModal, setShowScadenzaModal] = useState(false);
  const [editingScadenza, setEditingScadenza] = useState({
    scadenzaOfferte: '',
    note: ''
  });
  const [loadingScadenza, setLoadingScadenza] = useState(false);
  
  // Stati per il caricamento massivo delle offerte
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFileType, setUploadFileType] = useState('');
  const [parsedData, setParsedData] = useState([]);
  const [matchingStatus, setMatchingStatus] = useState({
    vetture: [],
    errors: [],
    warnings: [],
    ready: false
  });
  const [uploadStep, setUploadStep] = useState(1);

  // Stati per la gestione delle offerte in evidenza
  const [showOfferteEvidenzaModal, setShowOfferteEvidenzaModal] = useState(false);
  const [offerteInEvidenza, setOfferteInEvidenza] = useState([]);
  const [sitiVetrina, setSitiVetrina] = useState([]);
  const [loadingOfferteEvidenza, setLoadingOfferteEvidenza] = useState(false);
  const [selectedSitoVetrina, setSelectedSitoVetrina] = useState('globali');
  const [selectedOfferte, setSelectedOfferte] = useState([]);
  const [availableOfferte, setAvailableOfferte] = useState([]);
  
  // Nuovi stati per il miglioramento della modale offerte in evidenza
  const [searchOfferteEvidenza, setSearchOfferteEvidenza] = useState('');
  const [filtroMarcaEvidenza, setFiltroMarcaEvidenza] = useState('');
  const [filtroDurataEvidenza, setFiltroDurataEvidenza] = useState('');
  const [filtroTipoClienteEvidenza, setFiltroTipoClienteEvidenza] = useState('');
  const [offerteGroupedByVettura, setOfferteGroupedByVettura] = useState({});
  
  // Nuovi stati per monitorare l'importazione
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');
  const [importedCount, setImportedCount] = useState(0);
  const [totalToImport, setTotalToImport] = useState(0);
  
  // Nuovi stati per la gestione dei campi - inizializzati vuoti per caricare solo dal DB
  const [penaliPresets, setPenaliPresets] = useState({
    penaleKasko: [],
    penaleRCA: [],
    penaleFurto: [],
    servizi: [],
    disponibilita: ["Stock", "Pronta Consegna", "Ordine", "Usato"] // Valori per statoDisponibilita
  });
  const [isCustomPenaleValue, setIsCustomPenaleValue] = useState({});

  // Nuovo stato per il modale delle operazioni
  const [showOperationsModal, setShowOperationsModal] = useState(false);
  
  // Nuovo stato per la conferma del truncate
  const [truncateConfirmStage, setTruncateConfirmStage] = useState(0);
  
  // Stato per il loading del truncate
  const [truncateLoading, setTruncateLoading] = useState(false);

  // Tipi di clienti disponibili
  const tipiClienti = ['Privato', 'Azienda', 'Tutti'];
  
  // Stato offerta: attiva o inattiva
  const statoOffertaOpzioni = [
    { value: true, label: 'Attiva' },
    { value: false, label: 'Inattiva' }
  ];

  // Recupera il conteggio delle offerte per ciascuna vettura
  const fetchOfferteCount = async (vetturaId) => {
    try {
      const response = await api.get(`/offerte-vetture/count?idVettura=${vetturaId}`);
      return response.data.count || 0;
    } catch (err) {
      console.error(`Errore nel conteggio offerte per vettura ${vetturaId}:`, err);
      return 0;
    }
  };

  // Funzione per caricare le marche da un'API dedicata
  const fetchBrands = async () => {
    try {
      const response = await api.get('/vehicles/brands');
      //console.log('Risposta API marche:', response.data);
      
      let marcheList = [];
      if (Array.isArray(response.data)) {
        marcheList = response.data.map(marca => marca.descrizione).filter(Boolean);
      } else if (response.data && Array.isArray(response.data.brands)) {
        marcheList = response.data.brands.map(marca => marca.descrizione).filter(Boolean);
      }
      
      setMarche(marcheList);
    } catch (err) {
      console.error('Errore nel caricamento delle marche:', err);
    }
  };

  // Funzione per caricare le offerte di una specifica vettura, memorizzata con useCallback
  const fetchOfferte = useCallback(async () => {
    // Evita chiamate multiple durante il caricamento
    if (loading) {
      console.log('Caricamento già in corso, ignoro la chiamata');
      return;
    }
    
    setLoading(true);
    try {
      // Rimuoviamo il parametro limit=0 che potrebbe non essere supportato
      // e aggiungiamo più logging per il debug
      //console.log(`Caricamento offerte per vettura ID: ${selectedVetturaId}`);
      const response = await api.get(`/offerte-vetture?idVettura=${selectedVetturaId}&all=true&limit=1000`, {
        timeout: 15000 // 15 secondi di timeout
      });
      //console.log('Risposta API offerte:', response.data);
      
      let offerteData = [];
      
      // Gestione più robusta delle diverse possibili strutture di risposta
      if (response.data && Array.isArray(response.data.offerte)) {
        offerteData = response.data.offerte;
      } else if (response.data && Array.isArray(response.data)) {
        offerteData = response.data;
      } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        // Tenta di recuperare le offerte da qualche proprietà dell'oggetto
        const possibleArrayProps = Object.values(response.data).filter(val => Array.isArray(val));
        if (possibleArrayProps.length > 0) {
          // Usa l'array più lungo trovato
          offerteData = possibleArrayProps.reduce((a, b) => a.length > b.length ? a : b, []);
        }
      }
      
      //console.log(`Trovate ${offerteData.length} offerte per la vettura`);
      
      // Assicuriamoci che tutti i dati numerici siano effettivamente numerici
      const processedOfferte = offerteData.map(offerta => {
        // Converti esplicitamente i campi numerici
        return {
          ...offerta,
          durata: typeof offerta.durata === 'string' ? parseInt(offerta.durata, 10) || 0 : offerta.durata || 0,
          kmAnnui: typeof offerta.kmAnnui === 'string' ? parseInt(offerta.kmAnnui, 10) || 0 : offerta.kmAnnui || 0,
          canone: typeof offerta.canone === 'string' ? parseFloat(offerta.canone) || 0 : offerta.canone || 0,
          penaleKasko: typeof offerta.penaleKasko === 'string' ? parseFloat(offerta.penaleKasko) || 0 : offerta.penaleKasko || 0,
          penaleRCA: typeof offerta.penaleRCA === 'string' ? parseFloat(offerta.penaleRCA) || 0 : offerta.penaleRCA || 0,
          penaleFurto: typeof offerta.penaleFurto === 'string' ? parseFloat(offerta.penaleFurto) || 0 : offerta.penaleFurto || 0,
          settimaneConsegnaVettura: typeof offerta.settimaneConsegnaVettura === 'string' ? 
            parseInt(offerta.settimaneConsegnaVettura, 10) || 0 : offerta.settimaneConsegnaVettura || 0,
          // Assicurati che servizi sia una stringa JSON valida
          servizi: offerta.servizi ? (
            typeof offerta.servizi === 'string' ? offerta.servizi : JSON.stringify(offerta.servizi)
          ) : JSON.stringify([])
        };
      });
      
      setOfferte(processedOfferte);
      setError(null);
    } catch (err) {
      console.error('Errore nel caricamento delle offerte:', err);
      setError('Errore nel caricamento delle offerte. Riprova più tardi.');
      setOfferte([]);
    } finally {
      setLoading(false);
    }
  }, [selectedVetturaId]);
  
  // Nuova funzione per caricare le vetture con paginazione, memorizzata con useCallback
  const fetchVetture = useCallback(async () => {
    setLoadingVetture(true);
    try {
      // Se il filtro "con offerte" è attivo, carica TUTTE le vetture per poi filtrarle lato client
      const params = {
        page: filterVettureConOfferte ? 1 : pagination.currentPage,
        limit: filterVettureConOfferte ? 10000 : pagination.limit, // Numero molto alto per prendere tutto
        search: searchQuery,
        marca_id: filterMarca
      };
      
      const vettureRes = await api.get('/vehicles', { params });
      //console.log('Risposta API vetture:', vettureRes.data);
        
        // Controlla se la risposta è un array o se contiene un array
        let vettureArray = [];
        let serverPagination = null;
        if (Array.isArray(vettureRes.data)) {
          vettureArray = vettureRes.data;
        } else if (vettureRes.data && Array.isArray(vettureRes.data.vehicles)) {
          vettureArray = vettureRes.data.vehicles;
          // Paginazione del server: applicata solo quando NON si filtra lato client
          serverPagination = {
            currentPage: vettureRes.data.currentPage || 1,
            totalPages: vettureRes.data.totalPages || 1,
            total: vettureRes.data.total || 0
          };
        } else if (vettureRes.data && typeof vettureRes.data === 'object') {
          // Per altri formati di risposta, prova a convertire l'oggetto in array
          vettureArray = Object.values(vettureRes.data).filter(item => item && typeof item === 'object');
        }
        
        // NUOVO: Carica il conteggio delle offerte per tutte le vetture in una sola chiamata
        const offerteCounts = {};
        if (vettureArray.length > 0) {
          try {
            const vettureIds = vettureArray.map(v => v.id);
            const countsRes = await api.post('/offerte-vetture/counts-batch', { vettureIds });
            
            if (countsRes.data && countsRes.data.success) {
              // Mappa i conteggi ricevuti
              countsRes.data.counts.forEach(item => {
                offerteCounts[item.idVettura] = item.count;
              });
            } else {
              // Fallback: calcola i conteggi individualmente
              for (const vettura of vettureArray) {
                offerteCounts[vettura.id] = await fetchOfferteCount(vettura.id);
              }
            }
          } catch (countsError) {
            console.log('Errore nel caricamento conteggi batch, uso il metodo singolo:', countsError);
            // Fallback: calcola i conteggi individualmente
            for (const vettura of vettureArray) {
              offerteCounts[vettura.id] = await fetchOfferteCount(vettura.id);
            }
          }
        }
        
        // Filtra le vetture se è richiesto solo quelle con offerte
        let vettureToShow = vettureArray;

        if (filterVettureConOfferte) {
          // Sono state caricate tutte le vetture (limit alto): filtra quelle con offerte
          const vettureConOfferte = vettureArray.filter(vettura => (offerteCounts[vettura.id] || 0) > 0);

          // Paginazione lato client MANTENENDO la pagina scelta dall'utente
          const totalToShow = vettureConOfferte.length;
          const totalPagesToShow = Math.max(1, Math.ceil(totalToShow / pagination.limit));
          const paginaCorrente = Math.min(pagination.currentPage, totalPagesToShow);
          const startIndex = (paginaCorrente - 1) * pagination.limit;
          vettureToShow = vettureConOfferte.slice(startIndex, startIndex + pagination.limit);

          setPagination(prev => ({
            ...prev,
            currentPage: paginaCorrente,
            totalPages: totalPagesToShow,
            total: totalToShow
          }));
        } else if (serverPagination) {
          // Paginazione gestita dal server
          setPagination(prev => ({ ...prev, ...serverPagination }));
        }

        setVetture(vettureArray);
        setVettureFiltered(vettureToShow);
        setVettureOfferteCount(offerteCounts);
        setLoadingVetture(false);
    } catch (err) {
      console.error('Errore nel caricamento delle vetture:', err);
      setError('Errore nel caricamento delle vetture. Riprova più tardi.');
      setLoadingVetture(false);
    }
  }, [pagination.currentPage, pagination.limit, searchQuery, filterMarca, filterVettureConOfferte]);

  // Carica le offerte, le vetture e le società di noleggio
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Carica le marche
        await fetchBrands();
        
        // Carica le società di noleggio
        const societaRes = await api.get('/noleggio/societanoleggio');
        //console.log('Risposta API società:', societaRes.data); // Debug per vedere la struttura
        
        if (societaRes.data && Array.isArray(societaRes.data.societa)) {
          setSocieta(societaRes.data.societa);
          //console.log(`✅ Caricate ${societaRes.data.societa.length} società:`, societaRes.data.societa.map(s => ({id: s.id, nome: s.nome})));
        } else if (societaRes.data && Array.isArray(societaRes.data)) {
          setSocieta(societaRes.data);
          //console.log(`✅ Caricate ${societaRes.data.length} società:`, societaRes.data.map(s => ({id: s.id, nome: s.nome})));
        } else {
          console.error('Formato dati società non valido:', societaRes.data);
          setSocieta([]);
        }

        // Carica le categorie offerte
        try {
          const categorieRes = await api.get('/categorie-offerte', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          if (categorieRes.data && categorieRes.data.success) {
            setCategorie(categorieRes.data.data || []);
            //console.log(`✅ Caricate ${categorieRes.data.data?.length || 0} categorie:`, categorieRes.data.data?.map(c => ({id: c.id, nome: c.nome})));
          }
        } catch (error) {
          console.error('Errore nel caricamento categorie:', error);
          setCategorie([]);
        }

        // Non caricare le offerte qui, verrà fatto dal useEffect separato
        setLoading(false);
      } catch (err) {
        console.error('Errore nel caricamento dei dati:', err);
        setError('Errore nel caricamento dei dati. Riprova più tardi.');
        setLoading(false);
      }
    };

    // Carica sempre i dati di base (società, categorie, marche) indipendentemente dalla vettura
    fetchData();
  }, []); // Carica solo all'avvio del componente

  // Carica automaticamente la vettura dall'URL se presente
  useEffect(() => {
    const vetturaIdFromUrl = searchParams.get('vettura');
    if (vetturaIdFromUrl && !loading && !selectedVetturaId && societa.length > 0) {
      console.log(`🔗 [URL PARAMS] Selezione vettura ID ${vetturaIdFromUrl} dall'URL`);
      
      const loadAndSelectVettura = async () => {
        try {
          const vetturaId = parseInt(vetturaIdFromUrl);
          
          // Carica la lista completa delle vetture
          console.log('📡 Carico lista vetture per trovare ID:', vetturaId);
          const response = await api.get('/vehicles', {
            params: { page: 1, limit: 10000 }
          });
          
          let vettureArray = [];
          if (Array.isArray(response.data)) {
            vettureArray = response.data;
          } else if (response.data && Array.isArray(response.data.vehicles)) {
            vettureArray = response.data.vehicles;
          } else if (response.data && response.data.data && Array.isArray(response.data.data.vehicles)) {
            vettureArray = response.data.data.vehicles;
          }
          
          console.log(`📋 Trovate ${vettureArray.length} vetture`);
          const vettura = vettureArray.find(v => v.id === vetturaId);
          
          if (vettura) {
            console.log('✅ Vettura trovata, selezione in corso');
            setSelectedVetturaId(vettura.id);
            setSelectedVettura(vettura);
            fetchScadenza(vettura.id);
            setNewRow(null);
            setEditingRowId(null);
            setOfferte([]);
            toast.success('Vettura selezionata dalla notifica');
          } else {
            console.error(`❌ Vettura ${vetturaId} non trovata`);
            toast.error('Vettura non trovata');
          }
          
        } catch (error) {
          console.error('❌ Errore caricamento vettura:', error);
          toast.error('Errore nel caricamento della vettura');
        }
        
        setSearchParams({});
      };
      
      loadAndSelectVettura();
    }
  }, [searchParams, loading, selectedVetturaId, societa, setSearchParams]);

  // Carica le offerte quando cambia la vettura selezionata
  useEffect(() => {
    if (selectedVetturaId) {
      fetchOfferte();
    }
  }, [selectedVetturaId, fetchOfferte]);

  // Carica le vetture in modo paginato quando cambia la paginazione o i filtri
  // o quando viene aperta la modale
  useEffect(() => {
    if (showModal) {
      fetchVetture();
    }
  }, [pagination.currentPage, searchQuery, filterMarca, showModal, fetchVetture]);
  
  // Funzione per cambiare pagina
  const handlePageChange = (page) => {
    setPagination({
      ...pagination,
      currentPage: page
    });
  };
  
  // Funzione per aprire la modale di selezione vettura
  const openVetturaModal = () => {
    setShowModal(true);
    // Resetta filtri
    setSearchQuery('');
    setFilterMarca('');
    // Prima pagina
    setPagination({
      ...pagination,
      currentPage: 1
    });
  };
  
  // Funzione per chiudere la modale
  const closeVetturaModal = () => {
    setShowModal(false);
  };
  
  // Funzione per selezionare una vettura dalla tabella
  const handleSelectVettura = (vettura) => {
    setSelectedVetturaId(vettura.id);
    setSelectedVettura(vettura);
    
    // Recupera la scadenza della vettura selezionata
    fetchScadenza(vettura.id);
    
    // Resetta gli stati di modifica
    setNewRow(null);
    setEditingRowId(null);
    setOfferte([]);
    
    // Chiudi la modale
    setShowModal(false);
  };

  // La funzione formatOfferteCount è stata rimossa perché non utilizzata

  // Funzione per eliminare un'offerta
  const handleDelete = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questa offerta?')) {
      try {
        await api.delete(`/offerte-vetture/${id}`);
        toast.success('Offerta eliminata con successo');
        fetchOfferte();
      } catch (err) {
        console.error('Errore nell\'eliminazione dell\'offerta:', err);
        toast.error('Errore nell\'eliminazione dell\'offerta');
      }
    }
  };

  // Inizializza una nuova riga
  const initNewRow = () => {
    setNewRow({
      idVettura: selectedVetturaId,
      idSocietaNoleggio: '',
      tipoCliente: 'Privato',
      categoriaId: '',
      durata: '',
      kmAnnui: '',
      canone: '',
      anticipo: '0',
      provvigione: '0',
      penaleKasko: '',
      penaleRCA: '',
      penaleFurto: '',
      servizi: '[]',
      coloreVettura: '',
      settimaneConsegnaVettura: '',
      optionalVettura: '',
      statoOfferta: true,
      statoDisponibilita: 'Ordine' // Default per il nuovo campo
    });
  };

  // Inizializza l'editing di una riga esistente
  const handleEdit = (offerta) => {
    setEditingRowId(offerta.id);
  };

  // Annulla l'editing o la creazione
  const handleCancel = () => {
    setNewRow(null);
    setEditingRowId(null);
  };

  // Gestisce il cambiamento nei campi della nuova riga o della riga in modifica
  const handleRowChange = (e, isNewRow = true) => {
    const { name, value, type, checked } = e.target;
    
    // Per checkbox
    if (type === 'checkbox') {
      if (isNewRow) {
        setNewRow({
          ...newRow,
          [name]: checked
        });
      } else {
        const updatedOfferte = offerte.map(off => 
          off.id === editingRowId 
            ? { ...off, [name]: checked } 
            : off
        );
        setOfferte(updatedOfferte);
      }
      return;
    }
    
    // Per campi numerici, assicuriamoci che settimaneConsegnaVettura sia sempre 0 se vuoto
    if (type === 'number' || name === 'durata' || name === 'kmAnnui' || name === 'canone' || 
        name === 'penaleKasko' || name === 'penaleRCA' || name === 'penaleFurto' || 
        name === 'provvigione') {
      const numValue = value === '' ? '' : Number(value);
      if (isNewRow) {
        setNewRow({
          ...newRow,
          [name]: numValue
        });
      } else {
        const updatedOfferte = offerte.map(off => 
          off.id === editingRowId 
            ? { ...off, [name]: numValue } 
            : off
        );
        setOfferte(updatedOfferte);
      }
      return;
    }
    
    // Caso specifico per settimaneConsegnaVettura
    if (name === 'settimaneConsegnaVettura') {
      const numValue = value === '' ? 0 : Number(value);
      if (isNewRow) {
        setNewRow({
          ...newRow,
          [name]: numValue
        });
      } else {
        const updatedOfferte = offerte.map(off => 
          off.id === editingRowId 
            ? { ...off, [name]: numValue } 
            : off
        );
        setOfferte(updatedOfferte);
      }
      return;
    }
    
    // Per campi testuali
    if (isNewRow) {
      setNewRow({
        ...newRow,
        [name]: value
      });
    } else {
      const updatedOfferte = offerte.map(off => 
        off.id === editingRowId 
          ? { ...off, [name]: value } 
          : off
      );
      setOfferte(updatedOfferte);
    }
  };

  // Valida i campi obbligatori
  const validateRow = (row) => {
    if (!row.idVettura || !row.idSocietaNoleggio || !row.durata || 
        !row.kmAnnui || !row.canone || !row.tipoCliente) {
      toast.error('Tutti i campi obbligatori devono essere compilati');
      return false;
    }
    return true;
  };

  // Salva la nuova riga
  const handleSaveNew = async () => {
    if (!validateRow(newRow)) return;
    
    // Imposta i campi facoltativi a valori non-null e garantisci che settimaneConsegnaVettura sia un numero
    const rowToSave = {
      ...newRow,
      coloreVettura: newRow.coloreVettura || '',
      settimaneConsegnaVettura: newRow.settimaneConsegnaVettura === '' || newRow.settimaneConsegnaVettura === undefined ? 0 : Number(newRow.settimaneConsegnaVettura),
      optionalVettura: newRow.optionalVettura || ''
    };
    
    try {
      await api.post('/offerte-vetture', rowToSave);
      toast.success('Offerta creata con successo');
      setNewRow(null);
      fetchOfferte();
    } catch (err) {
      console.error('Errore nel salvataggio dell\'offerta:', err);
      toast.error('Errore nel salvataggio dell\'offerta: ' + (err.response?.data?.error || err.message));
    }
  };

  // Salva le modifiche a una riga esistente
  const handleSaveEdit = async () => {
    const offertaToUpdate = offerte.find(off => off.id === editingRowId);
    if (!validateRow(offertaToUpdate)) return;
    
    // Imposta i campi facoltativi a valori non-null e garantisci che settimaneConsegnaVettura sia un numero
    const rowToSave = {
      ...offertaToUpdate,
      coloreVettura: offertaToUpdate.coloreVettura || '',
      settimaneConsegnaVettura: offertaToUpdate.settimaneConsegnaVettura === '' || offertaToUpdate.settimaneConsegnaVettura === undefined ? 0 : Number(offertaToUpdate.settimaneConsegnaVettura),
      optionalVettura: offertaToUpdate.optionalVettura || ''
    };
    
    try {
      await api.put(`/offerte-vetture/${editingRowId}`, rowToSave);
      toast.success('Offerta aggiornata con successo');
      setEditingRowId(null);
      fetchOfferte();
    } catch (err) {
      console.error('Errore nell\'aggiornamento dell\'offerta:', err);
      toast.error('Errore nell\'aggiornamento dell\'offerta: ' + (err.response?.data?.error || err.message));
    }
  };

  // Funzione per ottenere l'URL dell'immagine della vettura
  const getImageUrl = (vehicle) => {
    if (!vehicle.img && !vehicle.immagine) return null;
    
    // Se abbiamo l'oggetto immagine completo, usa il suo path
    if (vehicle.immagine && vehicle.immagine.path_file) {
      return `${window.location.origin}${vehicle.immagine.path_file}`;
    }
    
    // Fallback per compatibilità: se img è un numero, è probabilmente un ID
    if (typeof vehicle.img === 'number') {
      return null; // Non possiamo mostrare un'immagine usando solo l'ID
    }
    
    // Se img è un path, usalo (caso legacy)
    if (typeof vehicle.img === 'string' && vehicle.img.startsWith('/')) {
      return `${window.location.origin}${vehicle.img}`;
    }
    
    return null;
  };
  
  // Converte le marche in formato utilizzabile dal SearchableSelect
  const getMarcheOptions = () => {
    return [
      { value: '', label: 'Tutte le marche' },
      ...marche.map((marca, index) => ({ value: (index + 1).toString(), label: marca }))
    ];
  };
  
  // Funzione per gestire il cambio di marca
  const handleMarcaChange = (e) => {
    setFilterMarca(e.target.value);
    console.log("Filtro marca impostato a:", e.target.value);
  };
  
  // Funzione per convertire un valore di penale (sia € che %) in un numero per il database
  const convertPenaleToNumber = (value) => {
    if (!value || value === 'Nessuna') return 0;
    
    const valueStr = String(value);
    
    // Se è una percentuale, salviamo il valore negativo per distinguerlo
    if (valueStr.includes('%')) {
      return -parseFloat(valueStr.replace('%', ''));
    }
    
    // Altrimenti, rimuoviamo il simbolo € e convertiamo in numero
    return parseFloat(valueStr.replace('€', ''));
  };
  
  // Funzione per convertire un valore numerico del DB in una stringa formattata
  const formatPenaleValue = (value, campo) => {
    if (!value || value === 0) return 'Nessuna';
    
    // Se è un valore negativo, è una percentuale
    if (value < 0) {
      return `${Math.abs(value)}%`;
    }
    
    // Altrimenti è un valore in euro
    return `${value}€`;
  };
  
  // Funzione per verificare se il valore è personalizzato
  const isCustomPenale = (value, campo) => {
    if (!value) return false;
    const formattedValue = typeof value === 'number' ? formatPenaleValue(value, campo) : value;
    return !penaliPresets[campo].includes(formattedValue);
  };
  
  // Funzione per navigare alla pagina di configurazione dei campi
const openConfigPage = () => {
  window.location.href = '/nlt/gestione-offerte-vetture/campi';
};
  
  // Funzione per caricare i preset dei campi dal database
  const fetchPenaliPresets = async () => {
    try {
      const response = await api.get('/offerte-vetture/config/campi-presets');
      if (response.data && response.data.presets) {
        setPenaliPresets(response.data.presets);
      }
    } catch (error) {
      console.error('Errore nel caricamento dei campi preimpostati:', error);
    }
  };
  
  // Carica i preset all'avvio
  useEffect(() => {
    fetchPenaliPresets();
  }, []);
  
  // Funzione modificata per gestire il cambiamento nei campi penale
  const handlePenaleChange = (e, campo, isNewRow) => {
    const { value } = e.target;
    
    // Se il valore è "Personalizzato", non aggiorniamo il campo ma impostiamo lo stato di personalizzazione
    if (value === 'custom') {
      setIsCustomPenaleValue(prev => ({
        ...prev,
        [isNewRow ? `new_${campo}` : `${editingRowId}_${campo}`]: true
      }));
      return;
    }
    
    // Altrimenti, aggiorniamo il campo normalmente
    const numValue = value === 'Nessuna' ? 0 : convertPenaleToNumber(value);
    
    if (isNewRow) {
      setNewRow({
        ...newRow,
        [campo]: numValue
      });
    } else {
      const updatedOfferte = offerte.map(off => 
        off.id === editingRowId 
          ? { ...off, [campo]: numValue } 
          : off
      );
      setOfferte(updatedOfferte);
    }
    
    // Resettiamo lo stato di personalizzazione
    setIsCustomPenaleValue(prev => ({
      ...prev,
      [isNewRow ? `new_${campo}` : `${editingRowId}_${campo}`]: false
    }));
  };
  
  // Funzione per gestire l'input personalizzato delle penali
  const handleCustomPenaleChange = (e, campo, isNewRow) => {
    const { value } = e.target;
    
    const valueStr = String(value);
    let processedValue = valueStr;
    // Controlliamo se l'utente sta inserendo una percentuale
    const isPercentage = valueStr.includes('%');
    
    // Rimuoviamo tutti i caratteri non numerici eccetto % alla fine
    processedValue = isPercentage 
      ? valueStr.replace(/[^0-9.%]/g, '').replace(/%/g, '') + '%'
      : valueStr.replace(/[^0-9.€]/g, '');
    
    // Convertiamo in valore numerico per salvare nel database
    const numValue = convertPenaleToNumber(processedValue);
    
    if (isNewRow) {
      setNewRow({
        ...newRow,
        [campo]: numValue
      });
    } else {
      const updatedOfferte = offerte.map(off => 
        off.id === editingRowId 
          ? { ...off, [campo]: numValue } 
          : off
      );
      setOfferte(updatedOfferte);
    }
  };
  
  // Funzione per gestire la selezione/deselezione dei servizi
  const handleServiziChange = (servizio, isNewRow = true) => {
    if (isNewRow) {
      const currentServizi = newRow.servizi ? JSON.parse(newRow.servizi) : [];
      let updatedServizi;
      
      if (currentServizi.includes(servizio)) {
        // Deseleziona il servizio
        updatedServizi = currentServizi.filter(s => s !== servizio);
      } else {
        // Seleziona il servizio
        updatedServizi = [...currentServizi, servizio];
      }
      
      setNewRow({
        ...newRow,
        servizi: JSON.stringify(updatedServizi)
      });
    } else {
      const offertaToUpdate = offerte.find(off => off.id === editingRowId);
      const currentServizi = offertaToUpdate.servizi ? JSON.parse(offertaToUpdate.servizi) : [];
      let updatedServizi;
      
      if (currentServizi.includes(servizio)) {
        // Deseleziona il servizio
        updatedServizi = currentServizi.filter(s => s !== servizio);
      } else {
        // Seleziona il servizio
        updatedServizi = [...currentServizi, servizio];
      }
      
      const updatedOfferte = offerte.map(off => 
        off.id === editingRowId 
          ? { ...off, servizi: JSON.stringify(updatedServizi) } 
          : off
      );
      
      setOfferte(updatedOfferte);
    }
  };

  // Funzione per ottenere l'elenco dei servizi selezionati
  const getServiziSelezionati = (serviziString) => {
    if (!serviziString) return [];
    try {
      return JSON.parse(serviziString);
    } catch (error) {
      console.error('Errore nel parsing dei servizi:', error);
      return [];
    }
  };
  
  // Funzione per aprire il modale di caricamento
  const openUploadModal = () => {
    setShowUploadModal(true);
    setUploadFile(null);
    setUploadStep(1);
    setParsedData([]);
    setMatchingStatus({
      vetture: [],
      errors: [],
      warnings: [],
      ready: false
    });
  };


  
  // Funzione per gestire la selezione del file
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadFile(file);
    
    // Determina il tipo di file
    const fileType = file.name.endsWith('.csv') ? 'csv' : 'excel';
    setUploadFileType(fileType);
  };
  
  // Funzione per elaborare il file CSV
  const parseCSVFile = (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          resolve(results.data);
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  };
  
  // Funzione per elaborare il file Excel
  const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        reject(error);
      };
      
      reader.readAsArrayBuffer(file);
    });
  };
  
  // Funzione per elaborare il file caricato
  const processUploadedFile = async () => {
    if (!uploadFile) return;
    
    try {
      let data;
      if (uploadFileType === 'csv') {
        data = await parseCSVFile(uploadFile);
      } else {
        data = await parseExcelFile(uploadFile);
      }
      
      setParsedData(data);
      
      // Carica i servizi con i loro ID per la mappatura
      let serviziConId = [];
      try {
        const response = await api.get('/offerte-vetture/config/campi-presets-with-ids');
        
        if (response.data && response.data.presetsWithIds && response.data.presetsWithIds.servizi) {
          serviziConId = response.data.presetsWithIds.servizi;
        } else {
          console.error('Nessun servizio trovato nella risposta API');
        }
      } catch (error) {
        console.error('Errore nel caricamento servizi con ID:', error);
      }
      
      // Elaborazione semplificata usando direttamente l'ID vettura
      const results = {
        offerte: [],
        errors: [],
        warnings: []
      };
      
      // Processa ogni riga
      data.forEach((row, index) => {
        try {
          // Estrai i dati dalla riga
          const idVettura = row.idVettura;
          const societaName = row.Società;
          
          if (!idVettura) {
            results.errors.push({
              row: index + 2,
              message: "Campo idVettura mancante"
            });
            return;
          }
          
          if (!societaName) {
            results.errors.push({
              row: index + 2,
              message: "Campo Società mancante"
            });
            return;
          }
          
          // Trova la società di noleggio - controlla sia per ID che per nome
          let matchedSocieta = null;
          
          // DEBUG: Log per vedere le società disponibili
          if (index === 0) { // Solo per la prima riga per non spammare i log
            console.log('🔍 DEBUG SOCIETÀ - Società disponibili:', societa.map(s => ({id: s.id, nome: s.nome})));
            console.log('🔍 DEBUG SOCIETÀ - Totale società caricate:', societa.length);
          }
          
          // Prima prova a cercare per ID numerico se societaName è un numero
          if (!isNaN(societaName)) {
            console.log(`🔍 DEBUG SOCIETÀ - Riga ${index + 2}: Cercando società con ID "${societaName}" (tipo: ${typeof societaName})`);
            console.log(`🔍 DEBUG SOCIETÀ - Società disponibili:`, societa.map(s => ({id: s.id, nome: s.nome})));
            matchedSocieta = societa.find(s => s.id === parseInt(societaName));
            console.log(`🔍 DEBUG SOCIETÀ - Riga ${index + 2}: Risultato ricerca per ID: ${matchedSocieta ? `Trovata: ${matchedSocieta.nome}` : 'Non trovata'}`);
          }
          
          // Se non trovato per ID, cerca per nome
          if (!matchedSocieta) {
            console.log(`🔍 DEBUG SOCIETÀ - Riga ${index + 2}: Cercando società per nome "${societaName}"`);
            matchedSocieta = societa.find(s => s.nome.toLowerCase().includes(String(societaName).toLowerCase()));
            console.log(`🔍 DEBUG SOCIETÀ - Riga ${index + 2}: Risultato ricerca per nome: ${matchedSocieta ? `Trovata: ${matchedSocieta.nome}` : 'Non trovata'}`);
          }
          
          if (!matchedSocieta) {
            results.warnings.push({
              row: index + 2,
              message: `Società "${societaName}" non trovata. Società disponibili: ${societa.map(s => `${s.nome} (ID: ${s.id})`).join(', ')}`
            });
            
            // Se è un ID numerico, offri di creare la società
            if (!isNaN(societaName) && parseInt(societaName) > 0) {
              results.suggestions = results.suggestions || [];
              results.suggestions.push({
                type: 'create_societa',
                id: parseInt(societaName),
                message: `Creare società con ID ${societaName}?`
              });
            }
            
            return;
          }
          
          // Elabora i servizi extra - mappiamo ID/nomi ai valoreCampo
          const serviziNomi = [];
          if (row['Servizi']) {
            const serviziValues = String(row['Servizi']).split(',').map(s => s.trim());
            
                        serviziValues.forEach(valoreServizio => {
              // Verifica se è già un ID numerico
              if (!isNaN(valoreServizio) && parseInt(valoreServizio) > 0) {
                const servizioId = parseInt(valoreServizio);
                // Verifica che l'ID esista nel database e prendi il valoreCampo
                const servizioEsistente = serviziConId.find(s => s.id === servizioId);
                
                if (servizioEsistente) {
                  serviziNomi.push(servizioEsistente.valoreCampo);
                } else {
                  // Se l'ID non viene trovato, prova a cercare per nome
                  // Mappa degli ID del CSV ai nomi dei servizi
                  const idToNameMap = {
                    209: 'Cambio Gomme Stagionale',
                    210: 'Manutenzione Ordinaria', 
                    207: 'Assistenza Stradale',
                    212: 'Auto Sostitutiva',
                    220: 'Manutenzione Straordinaria'
                  };
                  
                  const nomeServizio = idToNameMap[servizioId];
                  if (nomeServizio) {
                    const servizioPerNome = serviziConId.find(s => s.valoreCampo === nomeServizio);
                    if (servizioPerNome) {
                      serviziNomi.push(servizioPerNome.valoreCampo);
                      results.warnings.push({
                        row: index + 2,
                        message: `ID servizio "${servizioId}" non trovato, ma trovato per nome "${nomeServizio}"`
                      });
                    } else {
                      results.warnings.push({
                        row: index + 2,
                        message: `ID servizio "${servizioId}" non trovato nel database, verrà ignorato`
                      });
                    }
                  } else {
                    results.warnings.push({
                      row: index + 2,
                      message: `ID servizio "${servizioId}" non trovato nel database, verrà ignorato`
                    });
                  }
                }
              } else {
                // È un nome, quindi cerchiamo il nome esatto
                const valoreServizioStr = String(valoreServizio).toLowerCase();
                const servizioTrovato = serviziConId.find(s => 
                  s.valoreCampo.toLowerCase() === valoreServizioStr ||
                  s.valoreCampo.toLowerCase().includes(valoreServizioStr) ||
                  valoreServizioStr.includes(s.valoreCampo.toLowerCase())
                );
                
                if (servizioTrovato) {
                  serviziNomi.push(servizioTrovato.valoreCampo);
                } else {
                  results.warnings.push({
                    row: index + 2,
                    message: `Servizio "${valoreServizio}" non trovato nel database, verrà ignorato`
                  });
                }
              }
            });
          }
          
          // Funzione di supporto per convertire stringhe numeriche con separatori
          const parseNumberValue = (value) => {
            if (!value) return 0;
            
            // Se è una stringa con caratteri non numerici, estrai solo i numeri
            if (typeof value === 'string') {
              // Rimuovi tutti i caratteri che non sono numeri, punti o virgole
              const numericString = value.replace(/[^0-9.,]/g, '');
              // Sostituisci le virgole con punti decimali
              return parseFloat(numericString.replace(/\./g, '').replace(',', '.')) || 0;
            }
            
            return parseFloat(value) || 0;
          };
          
          // Gestisci il campo statoDisponibilita con validazione rispetto ai valori predefiniti
          const getStatoDisponibilita = (rowData) => {
            // Cerca in diverse possibili colonne
            const statoDisponibilitaValue = rowData['Disponibilita'] || rowData['DISPONIBILITA'] || 
                                           rowData['disponibilita'] || rowData['Stato Disponibilita'] || 
                                           rowData['STATO_DISPONIBILITA'] || rowData['statoDisponibilita'] || 
                                           rowData['StatoDisponibilita'] || rowData['Stato'] || 
                                           rowData['STATO'] || rowData['stato'];
            
            if (!statoDisponibilitaValue) return 'Ordine'; // Valore predefinito
            
            // Verifica che il valore sia tra quelli ammessi
            if (penaliPresets.disponibilita.length > 0) {
              // Cerca una corrispondenza esatta
              const exactMatch = penaliPresets.disponibilita.find(v => v.toLowerCase() === String(statoDisponibilitaValue).toLowerCase());
              if (exactMatch) return exactMatch;
              
              // Se non trova corrispondenza esatta, cerca una corrispondenza parziale
              const partialMatch = penaliPresets.disponibilita.find(v => 
                String(statoDisponibilitaValue).toLowerCase().includes(v.toLowerCase()) || 
                v.toLowerCase().includes(String(statoDisponibilitaValue).toLowerCase())
              );
              if (partialMatch) return partialMatch;
            }
            
            // Se il valore non è tra quelli predefiniti, aggiungi un warning
            results.warnings.push({
              row: index + 2,
              message: `Valore di stato disponibilità "${statoDisponibilitaValue}" non riconosciuto, verrà utilizzato tale e quale`
            });
            
            return statoDisponibilitaValue;
          };
          
          // Determina il valore booleano di statoOfferta
          const getStatoOfferta = (rowData) => {
            // Cerca in diverse possibili colonne
            const statoOffertaValue = rowData['Stato Offerta'] || rowData['STATO_OFFERTA'] || 
                                     rowData['statoOfferta'] || rowData['StatoOfferta'] || 
                                     rowData['Disponibile'] || rowData['DISPONIBILE'] || 
                                     rowData['disponibile'] || rowData['Attiva'] || 
                                     rowData['ATTIVA'] || rowData['attiva'];
            
            if (!statoOffertaValue) return true; // Default è attiva
            
            // Se è un valore booleano diretto, usalo
            if (typeof statoOffertaValue === 'boolean') return statoOffertaValue;
            
            // Se è una stringa, interpreta vari formati possibili
            if (typeof statoOffertaValue === 'string') {
              const lowerValue = statoOffertaValue.toLowerCase().trim();
              // Valori che consideriamo "false"
              if (['no', 'false', '0', 'non disponibile', 'non attiva', 'disattivata', 'n', 'inattiva'].includes(lowerValue)) {
                return false;
              }
              // Valori che consideriamo "true"
              if (['si', 'sì', 'yes', 'true', '1', 'disponibile', 'attiva', 'y', 's'].includes(lowerValue)) {
                return true;
              }
            }
            
            // Se è un numero, 0 è false, qualsiasi altro numero è true
            if (typeof statoOffertaValue === 'number') {
              return statoOffertaValue !== 0;
            }
            
            return true; // Default
          };
          
          // Estrai la scadenza offerta
          const scadenzaOffertaRaw = row['Scadenza Offerta'] || row['SCADENZA_OFFERTA'] || row['scadenzaOfferta'] || row['ScadenzaOfferta'];
          let scadenzaOfferta = null;
          
          if (scadenzaOffertaRaw) {
                         // Gestisci diversi formati di data
             const parseDate = (dateString) => {
               if (!dateString) return null;
               
               // Se è un numero (seriale Excel), convertilo in data
               if (typeof dateString === 'number') {
                 // Excel conta i giorni dal 1 gennaio 1900
                 // Ma ha un bug: considera il 1900 bisestile, quindi dobbiamo sottrarre 1 per date dopo il 28/2/1900
                 const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // 30 dicembre 1899
                 const date = new Date(excelEpoch.getTime() + dateString * 86400000);
                 if (!isNaN(date.getTime())) {
                   // Aggiusta per timezone per evitare che salvi un giorno prima
                   const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
                   return offsetDate.toISOString().split('T')[0];
                 }
               }
               
               // Rimuovi spazi extra
               const cleaned = dateString.toString().trim();
               
               // Formato italiano dd/mm/yyyy (come nel CSV: "30/6/2025")
               if (cleaned.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                 const [day, month, year] = cleaned.split('/');
                 // Usa Date.UTC per evitare problemi di timezone
                 const date = new Date(Date.UTC(year, month - 1, parseInt(day)));
                 if (!isNaN(date.getTime())) {
                   return date.toISOString().split('T')[0];
                 }
               }
               
               // Formato ISO yyyy-mm-dd
               if (cleaned.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
                 return cleaned;
               }
               
               // Prova a parsare come data generica
               const parsed = new Date(cleaned);
               if (!isNaN(parsed.getTime())) {
                 // Aggiusta per timezone
                 const offsetDate = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
                 return offsetDate.toISOString().split('T')[0];
               }
               
               return null;
             };
            
            scadenzaOfferta = parseDate(scadenzaOffertaRaw);
            
            if (!scadenzaOfferta) {
              results.warnings.push({
                row: index + 2,
                message: `Formato data scadenza non riconosciuto: "${scadenzaOffertaRaw}". Verrà ignorata.`
              });
            }
          }
          
          // Gestione della categoria - cerca sia per ID che per nome
          let categoriaId = null;
          const categoriaValue = row['Categoria'] || row['categoria'] || row['CATEGORIA'];
          if (categoriaValue && String(categoriaValue).trim()) {
            let categoriaFound = null;
            
            // Prima prova a cercare per ID numerico se categoriaValue è un numero
            if (!isNaN(categoriaValue)) {
              console.log(`🔍 DEBUG CATEGORIA - Riga ${index + 2}: Cercando categoria con ID "${categoriaValue}"`);
              console.log(`🔍 DEBUG CATEGORIA - Categorie disponibili:`, categorie.map(c => ({id: c.id, nome: c.nome})));
              categoriaFound = categorie.find(cat => cat.id === parseInt(categoriaValue));
              console.log(`🔍 DEBUG CATEGORIA - Riga ${index + 2}: Risultato ricerca per ID: ${categoriaFound ? `Trovata: ${categoriaFound.nome}` : 'Non trovata'}`);
            }
            
            // Se non trovato per ID, cerca per nome
            if (!categoriaFound) {
              console.log(`🔍 DEBUG CATEGORIA - Riga ${index + 2}: Cercando categoria per nome "${categoriaValue}"`);
              categoriaFound = categorie.find(cat => 
                cat.nome.toLowerCase() === String(categoriaValue).trim().toLowerCase()
              );
              console.log(`🔍 DEBUG CATEGORIA - Riga ${index + 2}: Risultato ricerca per nome: ${categoriaFound ? `Trovata: ${categoriaFound.nome}` : 'Non trovata'}`);
            }
            
            if (categoriaFound) {
              categoriaId = categoriaFound.id;
            } else {
              results.warnings.push({
                row: index + 2,
                message: `Categoria "${categoriaValue}" non trovata. L'offerta sarà creata senza categoria. Categorie disponibili: ${categorie.map(c => `${c.nome} (ID: ${c.id})`).join(', ')}`
              });
              
              // Se è un ID numerico, offri di creare la categoria
              if (!isNaN(categoriaValue) && parseInt(categoriaValue) > 0) {
                results.suggestions = results.suggestions || [];
                results.suggestions.push({
                  type: 'create_categoria',
                  id: parseInt(categoriaValue),
                  message: `Creare categoria con ID ${categoriaValue}?`
                });
              }
            }
          }

          // Crea l'oggetto offerta
          const offerta = {
            idVettura: idVettura,
            idSocietaNoleggio: matchedSocieta.id,
            societa: matchedSocieta,
            durata: parseInt(row.Durata) || 0,
            kmAnnui: parseInt(row['km annui']?.toString().replace(/\./g, '')) || 0,
            canone: parseNumberValue(row['CANONE i.e.']),
            anticipo: parseNumberValue(row['Anticipo'] || row['ANTICIPO'] || row['anticipo'] || row['Anticipo i.e.']),
            provvigione: parseNumberValue(row['Provvigione'] || row['PROVVIGIONE'] || row['provvigione'] || row['Provvigioni i.e.']),
            tipoCliente: row.Target || 'Privato',
            categoriaId: categoriaId, // Aggiungi il campo categoria
            penaleKasko: row.kasko === 'Nessuna' ? 0 : convertPenaleToNumber(row.kasko),
            penaleRCA: row.RCA === 'Nessuna' ? 0 : convertPenaleToNumber(row.RCA),
            penaleFurto: row['F/I'] === 'Nessuna' ? 0 : convertPenaleToNumber(row['F/I']),
            servizi: JSON.stringify(serviziNomi),
            coloreVettura: row['Colore Esterno'] || '',
            settimaneConsegnaVettura: parseInt(row['Settimane consegna']) || 0,
            optionalVettura: row['Optional Inclusi'] || '',
            statoOfferta: getStatoOfferta(row),
            statoDisponibilita: getStatoDisponibilita(row),
            // Aggiungi la scadenza per questa offerta (sarà usata per creare/aggiornare la scadenza vettura)
            scadenzaOfferta: scadenzaOfferta
          };
          
          results.offerte.push(offerta);
        } catch (err) {
          results.errors.push({
            row: index + 2,
            message: `Errore elaborazione riga: ${err.message}`
          });
        }
      });
      
      setMatchingStatus({
        vetture: results.offerte,
        errors: results.errors,
        warnings: results.warnings,
        ready: results.errors.length === 0 && results.offerte.length > 0
      });
      
      setUploadStep(2);
    } catch (error) {
      console.error('Errore durante l\'elaborazione del file:', error);
      toast.error(`Errore durante l'elaborazione del file: ${error.message}`);
    }
  };
  
  // Funzione per inviare le offerte al database
  const saveOfferte = async () => {
    if (!matchingStatus.vetture.length || !matchingStatus.ready) return;
    
    try {
      // Inizia il processo di importazione
      setIsImporting(true);
      setImportProgress(0);
      setImportedCount(0);
      setTotalToImport(matchingStatus.vetture.length);
      
      // Raggruppa le offerte per vettura e raccogli le scadenze
      const vettureGroups = {};
      const vettureScadenze = {}; // Per memorizzare la prima scadenza trovata per ogni vettura
      
      matchingStatus.vetture.forEach(offerta => {
        if (!vettureGroups[offerta.idVettura]) {
          vettureGroups[offerta.idVettura] = [];
        }
        
        // Se abbiamo una scadenza e non l'abbiamo ancora salvata per questa vettura
        if (offerta.scadenzaOfferta && !vettureScadenze[offerta.idVettura]) {
          vettureScadenze[offerta.idVettura] = offerta.scadenzaOfferta;
        }
        
        // Rimuovi campi temporanei non necessari per il salvataggio
        const { societa, scadenzaOfferta, ...offerData } = offerta;
        vettureGroups[offerta.idVettura].push(offerData);
      });
      
      // Calcola quante vetture totali abbiamo da processare
      const totalVehicles = Object.keys(vettureGroups).length;
      let processedVehicles = 0;
      let totalProcessedOffers = 0;
      
      // Invia le offerte raggruppate per ogni vettura
      for (const idVettura in vettureGroups) {
        const offersInGroup = vettureGroups[idVettura].length;
        setImportStatus(`Importazione offerte per vettura ${processedVehicles + 1} di ${totalVehicles}`);
        
        await api.post('/offerte-vetture/batch', {
          idVettura,
          offerte: vettureGroups[idVettura]
        });
        
        processedVehicles++;
        totalProcessedOffers += offersInGroup;
        setImportedCount(totalProcessedOffers);
        setImportProgress(Math.floor((totalProcessedOffers / matchingStatus.vetture.length) * 100));
      }
      
      // Ora gestisci le scadenze per le vetture che ne hanno una
      const scadenzeCount = Object.keys(vettureScadenze).length;
      if (scadenzeCount > 0) {
        setImportStatus(`Impostazione scadenze per ${scadenzeCount} vetture...`);
        
        let processedScadenze = 0;
        for (const idVettura in vettureScadenze) {
          try {
            const scadenzaData = vettureScadenze[idVettura];
            
            // Crea o aggiorna la scadenza per questa vettura
            await api.post('/offerte-vetture/scadenze', {
              idVettura: parseInt(idVettura),
              scadenzaOfferte: scadenzaData,
              note: `Scadenza impostata automaticamente durante l'importazione CSV`
            });
            
            processedScadenze++;
            console.log(`✅ Scadenza impostata per vettura ${idVettura}: ${scadenzaData}`);
          } catch (error) {
            console.error(`❌ Errore nell'impostazione della scadenza per vettura ${idVettura}:`, error);
            // Non bloccare l'importazione per errori di scadenza
          }
        }
        
        console.log(`📅 Scadenze processate: ${processedScadenze}/${scadenzeCount}`);
      }
      
      // Messaggio di successo finale
      const scadenzeMessage = scadenzeCount > 0 ? ` e ${scadenzeCount} scadenze` : '';
      toast.success(`Importate ${matchingStatus.vetture.length} offerte${scadenzeMessage} con successo`);
      setIsImporting(false);
      setShowUploadModal(false);
      
      // Se è selezionata una vettura, aggiorna le offerte
      if (selectedVetturaId) {
        fetchOfferte();
      }
    } catch (error) {
      console.error('Errore durante il salvataggio delle offerte:', error);
      toast.error(`Errore durante il salvataggio: ${error.message}`);
      setIsImporting(false);
    }
  };

  // Funzione per formattare i servizi per la visualizzazione
  const formatServizi = (serviziString) => {
    const servizi = getServiziSelezionati(serviziString);
    if (servizi.length === 0) return '-';
    return servizi.join(', ');
  };

  // Componente per mostrare i servizi in visualizzazione
  const ServiziVisualizer = ({ serviziString }) => {
    const servizi = getServiziSelezionati(serviziString);
    if (servizi.length === 0) return <span className="text-gray-400">-</span>;
    
    const count = servizi.length;
    
    return (
      <div>
        {count > 2 ? (
          <div className="flex flex-col">
            <div className="flex items-center">
              <span className="text-sm text-gray-900 truncate max-w-[150px]">
                {servizi[0]}
              </span>
              <span className="ml-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                +{count - 1}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {servizi.slice(1, 3).join(', ')}
              {count > 3 && '...'}
            </div>
          </div>
        ) : (
          <div className="flex flex-col space-y-1">
            {servizi.map((servizio, idx) => (
              <span key={idx} className="text-sm text-gray-900">
                {servizio}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Renderizza il form di editing per una riga
  const renderEditRow = (offerta, isNewRow = false) => {
    const currentData = isNewRow ? newRow : offerta;
    const rowId = isNewRow ? 'new' : offerta.id;
    
    // Ottiene i servizi selezionati
    const serviziSelezionati = getServiziSelezionati(currentData.servizi);
    
    // Determina quali campi sono personalizzati
    const isKaskoCustom = isCustomPenaleValue[`${rowId}_penaleKasko`] || 
      isCustomPenale(currentData.penaleKasko, 'penaleKasko');
    const isRcaCustom = isCustomPenaleValue[`${rowId}_penaleRCA`] || 
      isCustomPenale(currentData.penaleRCA, 'penaleRCA');
    const isFurtoCustom = isCustomPenaleValue[`${rowId}_penaleFurto`] || 
      isCustomPenale(currentData.penaleFurto, 'penaleFurto');
    
    // Gestisce il cambiamento di tutti i servizi insieme
    const handleServiziListChange = (updatedServiziList) => {
      if (isNewRow) {
        setNewRow({
          ...newRow,
          servizi: JSON.stringify(updatedServiziList)
        });
      } else {
        const updatedOfferte = offerte.map(off => 
          off.id === editingRowId 
            ? { ...off, servizi: JSON.stringify(updatedServiziList) } 
            : off
        );
        setOfferte(updatedOfferte);
      }
    };
    
    // Debugging dei dati correnti
    console.log('Dati correnti in renderEditRow:', currentData);
    
    return (
      <tr key={isNewRow ? 'new' : offerta.id} className="bg-blue-50">
        <td className="px-2 py-2 border border-gray-300">
          <select
            name="idSocietaNoleggio"
            value={currentData.idSocietaNoleggio}
            onChange={(e) => handleRowChange(e, isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
            required
          >
            <option value="">Seleziona società</option>
            {societa.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome} (ID: {s.id})
              </option>
            ))}
          </select>
        </td>
        <td className="px-2 py-2 border border-gray-300">
          <select
            name="tipoCliente"
            value={currentData.tipoCliente}
            onChange={(e) => handleRowChange(e, isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
            required
          >
            {tipiClienti.map((tipo) => (
              <option key={tipo} value={tipo}>
                {tipo}
              </option>
            ))}
          </select>
        </td>
        <td className="px-2 py-2 border border-gray-300">
          <select
            name="categoriaId"
            value={currentData.categoriaId || ''}
            onChange={(e) => handleRowChange(e, isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
          >
            <option value="">Seleziona categoria</option>
            {categorie.filter(cat => cat.attiva).map((categoria) => (
              <option key={categoria.id} value={categoria.id}>
                {categoria.nome}
              </option>
            ))}
          </select>
        </td>
        <td className="px-2 py-2 border border-gray-300">
          <input
            type="number"
            name="durata"
            value={currentData.durata}
            onChange={(e) => handleRowChange(e, isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
            min="1"
            required
          />
        </td>
        <td className="px-2 py-2 border border-gray-300">
          <input
            type="number"
            name="kmAnnui"
            value={currentData.kmAnnui}
            onChange={(e) => handleRowChange(e, isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
            min="1"
            required
          />
        </td>
        <td className="px-2 py-2 border border-gray-300">
          <input
            type="number"
            name="canone"
            value={currentData.canone}
            onChange={(e) => handleRowChange(e, isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
            min="0"
            step="0.01"
            required
          />
        </td>
        <td className="px-2 py-2 border border-gray-300">
          <input
            type="number"
            name="anticipo"
            value={currentData.anticipo || '0'}
            onChange={(e) => handleRowChange(e, isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
            min="0"
            step="0.01"
          />
        </td>
        <td className="px-2 py-2 border border-gray-300">
          <input
            type="number"
            name="provvigione"
            value={currentData.provvigione || '0'}
            onChange={(e) => handleRowChange(e, isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
            min="0"
            step="0.01"
          />
        </td>
        <td className="px-2 py-2 border border-gray-300">
          {isKaskoCustom ? (
          <input
              type="text"
              value={formatPenaleValue(currentData.penaleKasko, 'penaleKasko')}
              onChange={(e) => handleCustomPenaleChange(e, 'penaleKasko', isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
              placeholder="Importo o %"
            />
          ) : (
            <select
              name="penaleKasko"
              value={formatPenaleValue(currentData.penaleKasko, 'penaleKasko')}
              onChange={(e) => handlePenaleChange(e, 'penaleKasko', isNewRow)}
              className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
            >
              {penaliPresets.penaleKasko.map((val) => (
                <option key={`kasko-${val}`} value={val}>{val}</option>
              ))}
              <option value="custom">Personalizzato...</option>
            </select>
          )}
        </td>
        <td className="px-2 py-2 border border-gray-300">
          {isRcaCustom ? (
          <input
              type="text"
              value={formatPenaleValue(currentData.penaleRCA, 'penaleRCA')}
              onChange={(e) => handleCustomPenaleChange(e, 'penaleRCA', isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
              placeholder="Importo o %"
            />
          ) : (
            <select
              name="penaleRCA"
              value={formatPenaleValue(currentData.penaleRCA, 'penaleRCA')}
              onChange={(e) => handlePenaleChange(e, 'penaleRCA', isNewRow)}
              className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
            >
              {penaliPresets.penaleRCA.map((val) => (
                <option key={`rca-${val}`} value={val}>{val}</option>
              ))}
              <option value="custom">Personalizzato...</option>
            </select>
          )}
        </td>
        <td className="px-2 py-2 border border-gray-300">
          {isFurtoCustom ? (
          <input
              type="text"
              value={formatPenaleValue(currentData.penaleFurto, 'penaleFurto')}
              onChange={(e) => handleCustomPenaleChange(e, 'penaleFurto', isNewRow)}
              className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
              placeholder="Importo o %"
            />
          ) : (
            <select
            name="penaleFurto"
              value={formatPenaleValue(currentData.penaleFurto, 'penaleFurto')}
              onChange={(e) => handlePenaleChange(e, 'penaleFurto', isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
            >
              {penaliPresets.penaleFurto.map((val) => (
                <option key={`furto-${val}`} value={val}>{val}</option>
              ))}
              <option value="custom">Personalizzato...</option>
            </select>
          )}
        </td>
        <td className="px-2 py-2 border border-gray-300">
          <ServiziSelector
            servizi={penaliPresets.servizi}
            serviziSelezionati={serviziSelezionati}
            onChange={handleServiziListChange}
          />
        </td>
        <td className="px-2 py-2 border border-gray-300">
          <input
            type="text"
            name="coloreVettura"
            value={currentData.coloreVettura || ''}
            onChange={(e) => handleRowChange(e, isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
          />
        </td>
        <td className="px-2 py-2 border border-gray-300">
          <input
            type="number"
            name="settimaneConsegnaVettura"
            value={currentData.settimaneConsegnaVettura || ''}
            onChange={(e) => handleRowChange(e, isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
            min="0"
          />
        </td>
        <td className="px-2 py-2 border border-gray-300">
          <input
            type="text"
            name="optionalVettura"
            value={currentData.optionalVettura || ''}
            onChange={(e) => handleRowChange(e, isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
            placeholder="Optional..."
          />
        </td>
        <td className="px-2 py-2 whitespace-nowrap">
          <select
            name="statoOfferta"
            value={currentData.statoOfferta}
            onChange={(e) => handleRowChange(e, isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
          >
            {statoOffertaOpzioni.map((opzione) => (
              <option key={String(opzione.value)} value={opzione.value}>
                {opzione.label}
              </option>
            ))}
          </select>
        </td>
        <td className="px-2 py-2 border border-gray-300">
          <select
            name="statoDisponibilita"
            value={currentData.statoDisponibilita || 'Ordine'}
            onChange={(e) => handleRowChange(e, isNewRow)}
            className="w-full px-2 py-1 border-none focus:outline-none focus:ring-0 bg-white rounded"
          >
            {penaliPresets.disponibilita.map((val) => (
              <option key={`stato-${val}`} value={val}>{val}</option>
            ))}
          </select>
        </td>
        <td className="px-2 py-2 whitespace-nowrap text-right text-sm font-medium">
          <button
            onClick={isNewRow ? handleSaveNew : handleSaveEdit}
            className="text-green-600 hover:text-green-900 mr-1"
            title={isNewRow ? "Salva nuova offerta" : "Salva modifiche"}
          >
            <CheckIcon className="h-5 w-5" />
          </button>
          <button
            onClick={handleCancel}
            className="text-red-600 hover:text-red-900"
            title="Annulla"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </td>
      </tr>
    );
  };

  // Aggiungere funzioni per mostrare correttamente i valori nella tabella delle offerte
  const renderOfferteRow = (offerta) => {
    // Debugging dei dati dell'offerta
    //console.log('Rendering offerta:', offerta);

    // Assicuriamoci che i valori siano definiti prima di formattarli
    const penaleKaskoFormatted = offerta.penaleKasko !== undefined ? formatPenaleValue(offerta.penaleKasko, 'penaleKasko') : '-';
    const penaleRCAFormatted = offerta.penaleRCA !== undefined ? formatPenaleValue(offerta.penaleRCA, 'penaleRCA') : '-';
    const penaleFurtoFormatted = offerta.penaleFurto !== undefined ? formatPenaleValue(offerta.penaleFurto, 'penaleFurto') : '-';
    
    // Formatta il canone con gestione dei numeri
    const canoneFormatted = offerta.canone !== undefined && !isNaN(parseFloat(offerta.canone)) 
      ? `€${parseFloat(offerta.canone).toLocaleString('it-IT', { minimumFractionDigits: 2 })}` 
      : '€0.00';
    
    // Formatta l'anticipo con gestione dei numeri
    const anticipoFormatted = offerta.anticipo !== undefined && !isNaN(parseFloat(offerta.anticipo)) 
      ? `€${parseFloat(offerta.anticipo).toLocaleString('it-IT', { minimumFractionDigits: 2 })}` 
      : '€0.00';
    
    // Formatta la provvigione con gestione dei numeri
    const provvigioneFormatted = offerta.provvigione !== undefined && !isNaN(parseFloat(offerta.provvigione)) 
      ? `€${parseFloat(offerta.provvigione).toLocaleString('it-IT', { minimumFractionDigits: 2 })}` 
      : '€0.00';
    
    // Formatta km annui con gestione sicura
    const kmAnnuiFormatted = offerta.kmAnnui !== undefined && !isNaN(parseInt(offerta.kmAnnui))
      ? parseInt(offerta.kmAnnui).toLocaleString()
      : '0';
      
    // Trova la società di noleggio in modo sicuro
    const societaInfo = societa.find(s => s.id === offerta.idSocietaNoleggio);
    const societaNome = societaInfo?.nome || 'N/D';
    const societaId = societaInfo?.id;

    return (
      <tr key={offerta.id} className="hover:bg-gray-50">
        {showOfferIds && (
          <td className="px-6 py-4 whitespace-nowrap text-sm">
            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
              ID: {offerta.id}
            </span>
          </td>
        )}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          <div className="flex flex-col">
            <span className="font-medium">{societaNome}</span>
            {societaId && (
              <span className="text-xs text-gray-500">ID: {societaId}</span>
            )}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {offerta.tipoCliente ? (
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              offerta.tipoCliente.toLowerCase() === 'privato' ? 'bg-amber-100 text-amber-800' :
              offerta.tipoCliente.toLowerCase() === 'azienda' ? 'bg-indigo-100 text-indigo-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {offerta.tipoCliente}
            </span>
          ) : (
            <span className="text-gray-400 italic">N/D</span>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm">
          {offerta.categoria ? (
            <div className="flex items-center">
              <div 
                className="w-3 h-3 rounded-full mr-2 border border-gray-300"
                style={{ backgroundColor: offerta.categoria.colore || '#3B82F6' }}
                title={offerta.categoria.descrizione}
              ></div>
              <span className="text-gray-900">{offerta.categoria.nome}</span>
            </div>
          ) : (
            <span className="text-gray-400 italic">Nessuna categoria</span>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {offerta.durata || '0'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {kmAnnuiFormatted}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {canoneFormatted}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {anticipoFormatted}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {provvigioneFormatted}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {penaleKaskoFormatted === 'Nessuna' ? '-' : penaleKaskoFormatted}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {penaleRCAFormatted === 'Nessuna' ? '-' : penaleRCAFormatted}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {penaleFurtoFormatted === 'Nessuna' ? '-' : penaleFurtoFormatted}
        </td>
        <td className="px-6 py-4 text-sm">
          <ServiziVisualizer serviziString={offerta.servizi} />
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {offerta.coloreVettura || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {offerta.settimaneConsegnaVettura ? `${offerta.settimaneConsegnaVettura} settimane` : '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {offerta.optionalVettura || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
            ${offerta.statoOfferta ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {offerta.statoOfferta ? 'Attiva' : 'Inattiva'}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {offerta.statoDisponibilita || 'Ordine'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <button
            onClick={() => handleEdit(offerta)}
            disabled={newRow !== null || editingRowId !== null}
            className={`text-blue-600 hover:text-blue-900 mr-3 ${(newRow !== null || editingRowId !== null) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDelete(offerta.id)}
            disabled={newRow !== null || editingRowId !== null}
            className={`text-red-600 hover:text-red-900 ${(newRow !== null || editingRowId !== null) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </td>
      </tr>
    );
  };

  // Helper function to render file icon
  const DocumentIcon = ({ className }) => {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  };
  
  // Funzione per formattare la data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });
  };
  
  // Funzione per recuperare la scadenza di una vettura
  const fetchScadenza = async (vetturaId) => {
    if (!vetturaId) return;
    
    try {
      setLoadingScadenza(true);
      const response = await api.get(`/offerte-vetture/scadenze/vettura/${vetturaId}`);
      if (response.data) {
        setScadenzaOfferte(response.data);
      } else {
        setScadenzaOfferte(null);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        // Scadenza non trovata, è normale
        setScadenzaOfferte(null);
      } else {
        console.error('Errore nel recupero della scadenza:', error);
        toast.error('Errore nel recupero della scadenza');
      }
    } finally {
      setLoadingScadenza(false);
    }
  };
  
  // Funzione per aprire il modale di modifica scadenza
  const openScadenzaModal = () => {
    // Se c'è già una scadenza, prepopola il form
    if (scadenzaOfferte) {
      const scadenzaDate = scadenzaOfferte.scadenzaOfferte 
        ? new Date(scadenzaOfferte.scadenzaOfferte).toISOString().split('T')[0]
        : '';
      
      setEditingScadenza({
        scadenzaOfferte: scadenzaDate,
        note: scadenzaOfferte.note || ''
      });
    } else {
      // Altrimenti prepara un form vuoto
      const today = new Date();
      // Imposta la data di default a 6 mesi da oggi
      const sixMonthsLater = new Date(today);
      sixMonthsLater.setMonth(today.getMonth() + 6);
      
      setEditingScadenza({
        scadenzaOfferte: sixMonthsLater.toISOString().split('T')[0],
        note: ''
      });
    }
    
    setShowScadenzaModal(true);
  };
  
  // Funzione per salvare la scadenza
  const saveScadenza = async () => {
    if (!selectedVetturaId) {
      toast.error('Nessuna vettura selezionata');
      return;
    }
    
    if (!editingScadenza.scadenzaOfferte) {
      toast.error('Data di scadenza richiesta');
      return;
    }
    
    try {
      setLoadingScadenza(true);
      
      const payload = {
        idVettura: selectedVetturaId,
        scadenzaOfferte: editingScadenza.scadenzaOfferte,
        note: editingScadenza.note
      };
      
      await api.post('/offerte-vetture/scadenze', payload);
      toast.success('Scadenza salvata con successo');
      
      // Aggiorna la scadenza visualizzata
      await fetchScadenza(selectedVetturaId);
      
      // Chiudi il modale
      setShowScadenzaModal(false);
    } catch (error) {
      console.error('Errore nel salvataggio della scadenza:', error);
      toast.error('Errore nel salvataggio della scadenza');
    } finally {
      setLoadingScadenza(false);
    }
  };

  // Funzione per eliminare la scadenza
  const deleteScadenza = async () => {
    if (!scadenzaOfferte || !scadenzaOfferte.id) {
      return toast.error('Nessuna scadenza da eliminare');
    }
    
    if (!window.confirm('Sei sicuro di voler eliminare questa scadenza?')) {
      return;
    }
    
    try {
      setLoadingScadenza(true);
      await api.delete(`/offerte-vetture/scadenze/${scadenzaOfferte.id}`);
      toast.success('Scadenza eliminata con successo');
      
      // Aggiorna lo stato
      setScadenzaOfferte(null);
    } catch (error) {
      console.error('Errore nell\'eliminazione della scadenza:', error);
      toast.error('Errore nell\'eliminazione della scadenza');
    } finally {
      setLoadingScadenza(false);
    }
  };

  // Funzione per aprire il modale delle operazioni
  const openOperationsModal = () => {
    setShowOperationsModal(true);
    // Reset dello stato di conferma del truncate
    setTruncateConfirmStage(0);
  };
  
  // Funzione per effettuare il truncate della tabella OfferteVetture
  const truncateOfferteTable = async () => {
    // Prima conferma
    if (truncateConfirmStage === 0) {
      setTruncateConfirmStage(1);
      return;
    }
    
    // Seconda conferma
    if (truncateConfirmStage === 1) {
      setTruncateConfirmStage(2);
      return;
    }
    
    // Procedi con il truncate
    if (truncateConfirmStage === 2) {
      try {
        setTruncateLoading(true);
        
        // Mostra un messaggio di progresso
        toast.info('Operazione di svuotamento in corso... Questo potrebbe richiedere alcuni minuti.');
        
        // Chiamata API per il truncate con timeout più lungo
        const response = await api.delete('/offerte-vetture/truncate', {
          timeout: 120000 // 2 minuti di timeout
        });
        
        // Mostra i dettagli dell'operazione
        const message = response.data.message || 'Database offerte svuotato con successo';
        toast.success(message);
        
        console.log('Risposta truncate:', response.data);
        
        // Aggiorna la UI solo se è selezionata una vettura specifica
        // e ricarica le offerte in modo controllato
        if (selectedVetturaId) {
          // Ricarica le offerte in modo esplicito senza attivare useEffect
          try {
            const response = await api.get(`/offerte-vetture?idVettura=${selectedVetturaId}&all=true&limit=1000`);
            let offerteData = [];
            
            if (response.data && Array.isArray(response.data.offerte)) {
              offerteData = response.data.offerte;
            } else if (response.data && Array.isArray(response.data)) {
              offerteData = response.data;
            } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
              const possibleArrayProps = Object.values(response.data).filter(val => Array.isArray(val));
              if (possibleArrayProps.length > 0) {
                offerteData = possibleArrayProps.reduce((a, b) => a.length > b.length ? a : b, []);
              }
            }
            
            const processedOfferte = offerteData.map(offerta => ({
              ...offerta,
              durata: typeof offerta.durata === 'string' ? parseInt(offerta.durata, 10) || 0 : offerta.durata || 0,
              kmAnnui: typeof offerta.kmAnnui === 'string' ? parseInt(offerta.kmAnnui, 10) || 0 : offerta.kmAnnui || 0,
              canone: typeof offerta.canone === 'string' ? parseFloat(offerta.canone) || 0 : offerta.canone || 0,
              penaleKasko: typeof offerta.penaleKasko === 'string' ? parseFloat(offerta.penaleKasko) || 0 : offerta.penaleKasko || 0,
              penaleRCA: typeof offerta.penaleRCA === 'string' ? parseFloat(offerta.penaleRCA) || 0 : offerta.penaleRCA || 0,
              penaleFurto: typeof offerta.penaleFurto === 'string' ? parseFloat(offerta.penaleFurto) || 0 : offerta.penaleFurto || 0,
              settimaneConsegnaVettura: typeof offerta.settimaneConsegnaVettura === 'string' ? 
                parseInt(offerta.settimaneConsegnaVettura, 10) || 0 : offerta.settimaneConsegnaVettura || 0,
              servizi: offerta.servizi ? (
                typeof offerta.servizi === 'string' ? offerta.servizi : JSON.stringify(offerta.servizi)
              ) : JSON.stringify([])
            }));
            
            setOfferte(processedOfferte);
          } catch (error) {
            console.error('Errore nel ricaricamento delle offerte dopo truncate:', error);
            setOfferte([]);
          }
        }
        
        // Resetta lo stato di conferma e chiudi il modale
        setTruncateConfirmStage(0);
        setShowOperationsModal(false);
      } catch (error) {
        console.error('Errore nello svuotamento del database:', error);
        toast.error('Errore nello svuotamento del database: ' + (error.response?.data?.message || error.message));
      } finally {
        setTruncateLoading(false);
      }
    }
  };

  // Funzioni per gestire le offerte in evidenza
  const openOfferteEvidenzaModal = async () => {
    setShowOfferteEvidenzaModal(true);
    setLoadingOfferteEvidenza(true);
    
    try {
      // Carica i siti vetrina disponibili
      const sitiResponse = await api.get('/offerte-vetture/siti-vetrina');
      setSitiVetrina(sitiResponse.data.data || []);
      
      // Carica le offerte disponibili (tutte quelle attive)
      const offerteResponse = await api.get('/offerte-vetture?statoOfferta=true&all=true');
      const offerte = offerteResponse.data.data || [];
      setAvailableOfferte(offerte);
      
      // Raggruppa le offerte per vettura
      groupOfferteByVettura(offerte);
      
      // Carica le offerte in evidenza esistenti
      await fetchOfferteInEvidenza();
    } catch (error) {
      console.error('Errore nel caricamento dati offerte in evidenza:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoadingOfferteEvidenza(false);
    }
  };

  // Funzione per raggruppare le offerte per vettura
  const groupOfferteByVettura = (offerte) => {
    const grouped = {};
    
    offerte.forEach(offerta => {
      const vetturaId = offerta.vettura?.id;
      const vetturaKey = `${offerta.vettura?.marca?.descrizione} ${offerta.vettura?.modello}`;
      
      if (!grouped[vetturaId]) {
        grouped[vetturaId] = {
          vettura: offerta.vettura,
          vetturaKey,
          offerte: []
        };
      }
      
      grouped[vetturaId].offerte.push(offerta);
    });
    
    // Ordina le offerte all'interno di ogni gruppo per canone crescente
    Object.keys(grouped).forEach(vetturaId => {
      grouped[vetturaId].offerte.sort((a, b) => parseFloat(a.canone) - parseFloat(b.canone));
    });
    
    setOfferteGroupedByVettura(grouped);
  };

  // Funzione per filtrare le offerte raggruppate
  const getFilteredOfferteGrouped = () => {
    if (!offerteGroupedByVettura) return {};
    
    const filtered = {};
    
    Object.keys(offerteGroupedByVettura).forEach(vetturaId => {
      const group = offerteGroupedByVettura[vetturaId];
      
      // Filtra per ricerca testuale
      const searchMatch = !searchOfferteEvidenza || 
        group.vetturaKey.toLowerCase().includes(searchOfferteEvidenza.toLowerCase()) ||
        group.vettura?.allestimento?.toLowerCase().includes(searchOfferteEvidenza.toLowerCase());
      
      // Filtra per marca
      const marcaMatch = !filtroMarcaEvidenza || 
        group.vettura?.marca?.descrizione === filtroMarcaEvidenza;
      
      if (searchMatch && marcaMatch) {
        // Filtra le offerte all'interno del gruppo
        const offerteFiltered = group.offerte.filter(offerta => {
          const durataMatch = !filtroDurataEvidenza || 
            offerta.durata.toString() === filtroDurataEvidenza;
          
          const tipoClienteMatch = !filtroTipoClienteEvidenza || 
            offerta.tipoCliente === filtroTipoClienteEvidenza;
          
          return durataMatch && tipoClienteMatch;
        });
        
        if (offerteFiltered.length > 0) {
          filtered[vetturaId] = {
            ...group,
            offerte: offerteFiltered
          };
        }
      }
    });
    
    return filtered;
  };

  // Funzione per ottenere le opzioni uniche per i filtri
  const getFilterOptions = () => {
    const marche = new Set();
    const durate = new Set();
    const tipiCliente = new Set();
    
    availableOfferte.forEach(offerta => {
      if (offerta.vettura?.marca?.descrizione) {
        marche.add(offerta.vettura.marca.descrizione);
      }
      if (offerta.durata) {
        durate.add(offerta.durata.toString());
      }
      if (offerta.tipoCliente) {
        tipiCliente.add(offerta.tipoCliente);
      }
    });
    
    return {
      marche: Array.from(marche).sort(),
      durate: Array.from(durate).sort((a, b) => parseInt(a) - parseInt(b)),
      tipiCliente: Array.from(tipiCliente).sort()
    };
  };

  const fetchOfferteInEvidenza = async () => {
    try {
      const params = selectedSitoVetrina !== 'globali' ? { idSitoVetrina: selectedSitoVetrina } : {};
      const response = await api.get('/offerte-vetture/evidenza', { params });
      setOfferteInEvidenza(response.data.data || []);
    } catch (error) {
      console.error('Errore nel caricamento offerte in evidenza:', error);
      toast.error('Errore nel caricamento offerte in evidenza');
    }
  };

  const addOffertaInEvidenza = async (idOfferta) => {
    try {
      const payload = {
        idOfferta,
        idSitoVetrina: selectedSitoVetrina === 'globali' ? null : parseInt(selectedSitoVetrina)
      };
      
      await api.post('/offerte-vetture/evidenza', payload);
      toast.success('Offerta aggiunta in evidenza con successo');
      
      // Ricarica le offerte in evidenza
      await fetchOfferteInEvidenza();
    } catch (error) {
      console.error('Errore nell\'aggiunta dell\'offerta in evidenza:', error);
      const errorMessage = error.response?.data?.error || 'Errore nell\'aggiunta dell\'offerta in evidenza';
      toast.error(errorMessage);
    }
  };

  const removeOffertaInEvidenza = async (id) => {
    try {
      await api.delete(`/offerte-vetture/evidenza/${id}`);
      toast.success('Offerta rimossa dalla evidenza con successo');
      
      // Ricarica le offerte in evidenza
      await fetchOfferteInEvidenza();
    } catch (error) {
      console.error('Errore nella rimozione dell\'offerta in evidenza:', error);
      toast.error('Errore nella rimozione dell\'offerta in evidenza');
    }
  };

  const handleSitoVetrinaChange = async (newSito) => {
    setSelectedSitoVetrina(newSito);
    setLoadingOfferteEvidenza(true);
    
    try {
      const params = newSito !== 'globali' ? { idSitoVetrina: newSito } : {};
      const response = await api.get('/offerte-vetture/evidenza', { params });
      setOfferteInEvidenza(response.data.data || []);
    } catch (error) {
      console.error('Errore nel caricamento offerte in evidenza:', error);
      toast.error('Errore nel caricamento offerte in evidenza');
    } finally {
      setLoadingOfferteEvidenza(false);
    }
  };

  // Funzione per resettare i filtri
  const resetFiltersEvidenza = () => {
    setSearchOfferteEvidenza('');
    setFiltroMarcaEvidenza('');
    setFiltroDurataEvidenza('');
    setFiltroTipoClienteEvidenza('');
  };

  // Funzione per caricare l'immagine personalizzata per un'offerta in evidenza
  const uploadImmaginePersonalizzata = async (idOffertaEvidenza, file) => {
    try {
      const formData = new FormData();
      formData.append('immagine', file);

      await api.put(`/offerte-vetture/evidenza/${idOffertaEvidenza}/immagine`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Immagine personalizzata caricata con successo');
      
      // Ricarica le offerte in evidenza per mostrare l'immagine aggiornata
      await fetchOfferteInEvidenza();
    } catch (error) {
      console.error('Errore nel caricamento dell\'immagine personalizzata:', error);
      toast.error('Errore nel caricamento dell\'immagine personalizzata');
    }
  };

  // Funzione per gestire il caricamento del file immagine
  const handleImmagineUpload = (event, idOffertaEvidenza) => {
    const file = event.target.files[0];
    if (file) {
      // Verifica che sia un'immagine
      if (!file.type.startsWith('image/')) {
        toast.error('Seleziona un file immagine valido (PNG, JPG)');
        return;
      }
      
      // Verifica la dimensione (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('L\'immagine deve essere inferiore a 5MB');
        return;
      }

      uploadImmaginePersonalizzata(idOffertaEvidenza, file);
    }
  };

  // Funzione per creare una società mancante
  const createMissingSocieta = async (id, nome = null) => {
    try {
      const societaName = nome || `Società ${id}`;
      
      const response = await api.post('/noleggio/societanoleggio/with-id', {
        id: id,
        nome: societaName,
        attivo: true
      });
      
      toast.success(`Società "${societaName}" creata con ID ${id}`);
      
      // Ricarica le società
      const societaRes = await api.get('/noleggio/societanoleggio');
      if (societaRes.data && Array.isArray(societaRes.data.societa)) {
        setSocieta(societaRes.data.societa);
      } else if (societaRes.data && Array.isArray(societaRes.data)) {
        setSocieta(societaRes.data);
      }
      
      return response.data.societa;
    } catch (error) {
      console.error('Errore nella creazione della società:', error);
      toast.error('Errore nella creazione della società: ' + (error.response?.data?.message || error.message));
      return null;
    }
  };

  // Funzione per creare una categoria mancante
  const createMissingCategoria = async (id, nome = null) => {
    try {
      const categoriaName = nome || `Categoria ${id}`;
      
      const response = await api.post('/categorie-offerte', {
        id: id,
        nome: categoriaName,
        attiva: true
      });
      
      toast.success(`Categoria "${categoriaName}" creata con ID ${id}`);
      
      // Ricarica le categorie
      const categorieRes = await api.get('/categorie-offerte', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (categorieRes.data && categorieRes.data.success) {
        setCategorie(categorieRes.data.data || []);
      }
      
      return response.data;
    } catch (error) {
      console.error('Errore nella creazione della categoria:', error);
      toast.error('Errore nella creazione della categoria: ' + (error.response?.data?.message || error.message));
      return null;
    }
  };

  return (
    <div className="overflow-x-auto bg-white shadow-md rounded-lg p-6">
      {/* Intestazione */}
      <div className="flex flex-col md:flex-row justify-between mb-6 items-start md:items-center space-y-4 md:space-y-0">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Gestione Offerte Vetture</h2>
          <p className="text-sm text-gray-500">Gestisci le offerte di noleggio per le vetture disponibili</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={openOperationsModal}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <AdjustmentsHorizontalIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" /> 
            Operazioni
          </button>
          
          <button
            onClick={initNewRow}
            disabled={!selectedVetturaId || newRow !== null || editingRowId !== null}
            className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white 
              ${(!selectedVetturaId || newRow !== null || editingRowId !== null) 
                ? 'bg-gray-300 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'}`}
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nuova Offerta
          </button>
          
          <button
            onClick={() => fetchOfferte(pagination.currentPage)}
            disabled={loading || !selectedVetturaId || newRow !== null || editingRowId !== null}
            className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium
              ${(loading || !selectedVetturaId || newRow !== null || editingRowId !== null)
                ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                : 'text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500'}`}
          >
            <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      
      {/* Vettura selezionata */}
      <div className="mb-6">
        {selectedVettura ? (
          <div className="flex flex-col md:flex-row bg-white border border-blue-200 rounded-lg overflow-hidden shadow-sm">
            {/* Immagine della vettura (lato sinistro) */}
            <div className="w-full md:w-1/5 relative min-h-[140px] max-h-[180px] md:max-h-none flex-shrink-0">
              {getImageUrl(selectedVettura) ? (
                <img 
                  src={getImageUrl(selectedVettura)} 
                  alt={`${selectedVettura.marca?.descrizione || ''} ${selectedVettura.modello || ''}`}
                  className="w-full h-full object-contain absolute inset-0"
                  style={{ backgroundColor: '#f0f7ff' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='16' fill='%239ca3af'%3ENo Image%3C/text%3E%3C/svg%3E";
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-50 to-blue-100 p-4 flex items-center justify-center absolute inset-0">
                  <div className="h-20 w-20 bg-blue-200 rounded-full flex items-center justify-center">
                    <PhotoIcon className="h-10 w-10 text-blue-500" />
                  </div>
                </div>
              )}
            </div>
            
            {/* Dettagli della vettura (lato destro) */}
            <div className="w-full md:w-4/5 p-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium text-lg text-gray-800 flex items-center flex-wrap">
                  <span className="mr-2">Vettura Selezionata:</span> 
                  <span className="text-blue-600">
                    {selectedVettura.marca?.descrizione || ''} {selectedVettura.modello || ''} 
                    {selectedVettura.allestimento ? ` - ${selectedVettura.allestimento}` : ''}
                  </span>
                  {showVehicleIds && (
                    <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                      ID: {selectedVettura.id}
                    </span>
                  )}
                </h3>
                <div className="flex">
                  <button 
                    onClick={openScadenzaModal}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 border border-blue-300 rounded-md hover:bg-blue-50 flex-shrink-0 mr-2"
                  >
                    {scadenzaOfferte ? 'Modifica Scadenza' : 'Imposta Scadenza'}
                  </button>
                  <button 
                    onClick={openVetturaModal}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 border border-blue-300 rounded-md hover:bg-blue-50 flex-shrink-0"
                  >
                    Cambia vettura
                  </button>
                </div>
              </div>
              
              {/* Informazioni di scadenza */}
              {loadingScadenza ? (
                <div className="text-sm text-gray-500 mt-2">
                  Caricamento informazioni di scadenza...
                </div>
              ) : scadenzaOfferte ? (
                <div className="bg-blue-50 p-2 rounded-md mt-2 mb-3 border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-blue-800 mr-2">
                        Scadenza Offerte:
                      </span>
                      <span className="text-sm text-blue-700 font-bold">
                        {formatDate(scadenzaOfferte.scadenzaOfferte)}
                      </span>
                      {scadenzaOfferte.note && (
                        <span className="text-sm text-gray-600 ml-4">
                          Note: {scadenzaOfferte.note}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={deleteScadenza}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                      title="Elimina scadenza"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 p-2 rounded-md mt-2 mb-3 border border-yellow-100">
                  <div className="flex items-center">
                    <span className="text-sm text-yellow-700">
                      Nessuna scadenza impostata per le offerte di questa vettura
                    </span>
                  </div>
                </div>
              )}
              
              {/* Dettagli della vettura in una griglia compatta */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-2 text-sm mt-2">
                {selectedVettura.alimentazione && (
                  <div>
                    <span className="text-gray-500">Alimentazione:</span>
                    <span className="ml-1 text-gray-700">{selectedVettura.alimentazione}</span>
                  </div>
                )}
                {selectedVettura.categoria?.descrizione && (
                  <div>
                    <span className="text-gray-500">Categoria:</span>
                    <span className="ml-1 text-gray-700">{selectedVettura.categoria.descrizione}</span>
                  </div>
                )}
                {selectedVettura.trasmissione && (
                  <div>
                    <span className="text-gray-500">Trasmissione:</span>
                    <span className="ml-1 text-gray-700">{selectedVettura.trasmissione}</span>
                  </div>
                )}
                {selectedVettura.prezzo_listino && (
                  <div>
                    <span className="text-gray-500">Prezzo:</span>
                    <span className="ml-1 text-gray-700">
                      €{Number(selectedVettura.prezzo_listino).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {selectedVettura.potenza && (
                  <div>
                    <span className="text-gray-500">Potenza:</span>
                    <span className="ml-1 text-gray-700">{selectedVettura.potenza} CV</span>
                  </div>
                )}
                {vettureOfferteCount[selectedVettura.id] !== undefined && (
                  <div>
                    <span className="text-gray-500">Offerte:</span>
                    <span className="ml-1 text-gray-700">{vettureOfferteCount[selectedVettura.id]}</span>
                  </div>
                )}
              </div>
              
              {offerte.length > 0 && (
                <div className="mt-3 text-sm text-gray-500">
                  Questa vettura dispone di {offerte.length} {offerte.length === 1 ? 'offerta' : 'offerte'} di noleggio.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center text-gray-400 mb-2">
              <PhotoIcon className="h-10 w-10" />
            </div>
            <p className="text-gray-500 mb-4">Nessuna vettura selezionata</p>
            <button 
              onClick={openVetturaModal}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Seleziona vettura
            </button>
          </div>
        )}
      </div>

      {/* Modale per selezione vettura */}
      {showModal && (
        <div className="fixed z-[100] inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={closeVetturaModal}></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-7xl sm:w-full" style={{ zIndex: 300 }}>
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Seleziona Vettura
                  </h3>
                  <button
                    onClick={closeVetturaModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                
                {/* Filtri */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div style={{ zIndex: 200 }} className="relative">
                    <label className="block text-sm font-medium text-gray-500 mb-1">Marca</label>
                    <SearchableSelect
                      options={getMarcheOptions()}
                      value={filterMarca}
                      onChange={handleMarcaChange}
                      placeholder="Tutte le marche"
                      name="marca"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Ricerca</label>
                    <div className="relative rounded-md shadow-sm">
                      <input
                        type="text"
                        placeholder="Cerca per modello o allestimento..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-10"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between mb-4 mt-2">
                  <div className="flex items-center">
                    <input
                      id="show-id-checkbox"
                      type="checkbox"
                      checked={showVehicleIds}
                      onChange={(e) => setShowVehicleIds(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="show-id-checkbox" className="ml-2 text-sm text-gray-700">
                      Mostra ID Veicoli
                    </label>
                  </div>
                  
                  {/* NUOVO FILTRO: Solo vetture con offerte */}
                  <div className="flex items-center">
                    <input
                      id="filter-con-offerte-checkbox"
                      type="checkbox"
                      checked={filterVettureConOfferte}
                      onChange={(e) => setFilterVettureConOfferte(e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="filter-con-offerte-checkbox" className="ml-2 text-sm text-gray-700 font-medium">
                      Solo vetture con offerte
                    </label>
                  </div>
                </div>

                {/* Tabella delle vetture */}
                <div className="mt-4 overflow-hidden border rounded-lg">
                  <div className="overflow-x-auto max-h-[400px]" style={{ zIndex: 5 }}>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0" style={{ zIndex: 10 }}>
                        <tr className="border-b border-gray-200">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                            Immagine
                          </th>
                          {showVehicleIds && (
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                              ID
                            </th>
                          )}
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                            Marca
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                            Modello
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                            Allestimento
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                            Alimentazione
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                            Offerte
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0 right-0" style={{ zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                            Azioni
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {loadingVetture ? (
                          <tr>
                            <td colSpan="7" className="px-6 py-4 text-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                              <p className="mt-2 text-gray-500">Caricamento vetture...</p>
                            </td>
                          </tr>
                        ) : vettureFiltered.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                              Nessuna vettura trovata.
                            </td>
                          </tr>
                        ) : (
                          vettureFiltered.map((vettura) => (
                            <tr 
                              key={vettura.id} 
                              className={`hover:bg-gray-50 cursor-pointer ${selectedVetturaId === vettura.id ? 'bg-blue-50' : ''}`}
                              onClick={() => handleSelectVettura(vettura)}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                {getImageUrl(vettura) ? (
                                  <img 
                                    src={getImageUrl(vettura)} 
                                    alt={`${vettura.marca?.descrizione || ''} ${vettura.modello || ''}`}
                                    className="h-12 w-auto object-contain"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='60'%3E%3Crect width='100' height='60' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='10' fill='%239ca3af'%3ENo Image%3C/text%3E%3C/svg%3E";
                                    }}
                                  />
                                ) : (
                                  <div className="h-12 w-12 bg-gray-200 flex items-center justify-center text-gray-500">
                                    <PhotoIcon className="h-6 w-6 text-gray-400" />
                                  </div>
                                )}
                              </td>
                              {showVehicleIds && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {vettura.id}
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {vettura.marca?.descrizione || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {vettura.modello || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {vettura.allestimento || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {vettura.alimentazione || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {vettureOfferteCount[vettura.id] || 0}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  className="text-blue-600 hover:text-blue-900"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectVettura(vettura);
                                  }}
                                >
                                  Seleziona
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Paginazione */}
                {pagination.totalPages > 1 && (
                  <div className="flex justify-between items-center mt-4">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                        pagination.currentPage === 1
                          ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                          : 'text-gray-700 bg-white hover:bg-gray-50 border-gray-300'
                      }`}
                    >
                      <ChevronLeftIcon className="h-5 w-5 mr-1" />
                      Precedente
                    </button>
                    <span className="text-sm text-gray-700">
                      Pagina <span className="font-medium">{pagination.currentPage}</span> di{' '}
                      <span className="font-medium">{pagination.totalPages}</span>
                    </span>
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                        pagination.currentPage === pagination.totalPages
                          ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                          : 'text-gray-700 bg-white hover:bg-gray-50 border-gray-300'
                      }`}
                    >
                      Successiva
                      <ChevronRightIcon className="h-5 w-5 ml-1" />
                    </button>
            </div>
                )}
            </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {selectedVetturaId ? (
        loading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-3 text-gray-700 font-medium">Caricamento offerte...</p>
            <div className="mt-2 text-gray-500 text-sm max-w-md mx-auto">
              <p className="mb-1">• Recupero informazioni sulle società di noleggio</p>
              <p className="mb-1">• Caricamento dettagli delle offerte disponibili</p>
              <p className="mb-1">• Elaborazione penali e servizi inclusi</p>
            </div>
            <div className="mt-3 w-64 h-2 bg-gray-200 rounded-full mx-auto">
              <div className="h-full bg-blue-500 rounded-full animate-pulse"></div>
            </div>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-medium text-gray-700">Offerte di noleggio disponibili</h3>
              
              <div className="flex items-center">
                <input
                  id="show-offer-id-checkbox"
                  type="checkbox"
                  checked={showOfferIds}
                  onChange={(e) => setShowOfferIds(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="show-offer-id-checkbox" className="ml-2 text-sm text-gray-700">
                  Mostra ID Offerte
                </label>
              </div>
            </div>
            {/* Aggiunto un wrapper esterno con overflow nascosto per garantire che gli elementi fuori dal viewport non siano visibili */}
            <div className="relative overflow-hidden">
              <div className="overflow-x-auto max-h-[70vh]">
                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  {/* Header separato con stile più prominente */}
                  <thead className="bg-gray-50 sticky top-0" style={{ zIndex: 50, position: 'sticky', top: 0 }}>
                    <tr className="border-b border-gray-200">
                      {showOfferIds && (
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                          ID
                        </th>
                      )}
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Società di Noleggio*
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Tipo Cliente*
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Categoria
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Durata (mesi)*
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        KM Annui*
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Canone*
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Anticipo (€)
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Provvigione (€)
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Penale Kasko
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Penale RCA
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Penale Furto
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Servizi Inclusi
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Colore
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Tempo di consegna
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Optional inclusi
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Stato Offerta
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0" style={{ zIndex: 50, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Stato
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50" style={{ zIndex: 51, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        Azioni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {newRow && renderEditRow(null, true)}
                    
                    {offerte.length > 0 ? (
                      offerte.map((offerta) => (
                        editingRowId === offerta.id ? 
                          renderEditRow(offerta, false) :
                          renderOfferteRow(offerta)
                      ))
                    ) : (
                      <tr>
                        <td colSpan={showOfferIds ? "17" : "16"} className="px-6 py-8 text-center text-gray-500">
                          {newRow === null ? (
                            <div className="text-center">
                              <p className="mb-4">Nessuna offerta trovata per questa vettura.</p>
                              <button
                                onClick={initNewRow}
                                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                <PlusIcon className="h-5 w-5 mr-2" />
                                Crea la prima offerta
                              </button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <p className="text-gray-500 mb-4">Seleziona una vettura per visualizzare le relative offerte.</p>
        </div>
      )}

      {/* Modale per caricamento massivo offerte */}
      {showUploadModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowUploadModal(false)}></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {uploadStep === 1 ? 'Carica file offerte' : 
                     uploadStep === 2 ? 'Verifica offerte' : 
                     'Conferma importazione'}
                  </h3>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                
                {uploadStep === 1 && (
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        id="file-upload"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                      />
                      
                      {!uploadFile ? (
                        <div>
                          <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                          <p className="mt-2 text-sm text-gray-600">
                            Trascina qui il tuo file o{' '}
                            <button
                              onClick={() => fileInputRef.current.click()}
                              className="text-blue-600 hover:text-blue-500 font-medium"
                            >
                              seleziona un file
                            </button>
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            Formati supportati: CSV, Excel (.xlsx, .xls)
                          </p>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center justify-center">
                            <div className="bg-blue-50 rounded-lg p-3">
                              <DocumentIcon className="h-6 w-6 text-blue-500" />
                            </div>
                            <div className="ml-3 text-left">
                              <p className="text-sm font-medium text-gray-900">{uploadFile.name}</p>
                              <p className="text-xs text-gray-500">
                                {(uploadFile.size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setUploadFile(null);
                              fileInputRef.current.value = null;
                            }}
                            className="mt-3 text-sm text-red-600 hover:text-red-500"
                          >
                            Rimuovi file
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-end">
                      <button
                        onClick={() => processUploadedFile()}
                        disabled={!uploadFile}
                        className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                          ${!uploadFile ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'}`}
                      >
                        Analizza file
                      </button>
                    </div>
                  </div>
                )}
                
                {uploadStep === 2 && (
                  <div>
                    {/* Visualizza errori */}
                    {matchingStatus.errors.length > 0 && (
                      <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
                        <div className="flex">
                          <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2" />
                          <div>
                            <p className="text-sm text-red-700 font-medium">
                              Ci sono {matchingStatus.errors.length} errori che impediscono l'importazione
                            </p>
                            <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                              {matchingStatus.errors.slice(0, 5).map((error, idx) => (
                                <li key={idx}>
                                  Riga {error.row}: {error.message}
                                </li>
                              ))}
                              {matchingStatus.errors.length > 5 && (
                                <li>... e altri {matchingStatus.errors.length - 5} errori</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Visualizza avvisi */}
                    {matchingStatus.warnings.length > 0 && (
                      <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                        <div className="flex">
                          <ExclamationCircleIcon className="h-5 w-5 text-yellow-400 mr-2" />
                          <div className="flex-1">
                            <p className="text-sm text-yellow-700 font-medium">
                              Ci sono {matchingStatus.warnings.length} avvisi da verificare
                            </p>
                            <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
                              {matchingStatus.warnings.slice(0, 5).map((warning, idx) => (
                                <li key={idx}>
                                  Riga {warning.row}: {warning.message}
                                </li>
                              ))}
                              {matchingStatus.warnings.length > 5 && (
                                <li>... e altri {matchingStatus.warnings.length - 5} avvisi</li>
                              )}
                            </ul>
                            
                            {/* Pulsante per creare società mancante se ci sono avvisi di società non trovata */}
                            {matchingStatus.warnings.some(w => w.message.includes('Società') && w.message.includes('non trovata')) && (
                              <div className="mt-3">
                                <button
                                  onClick={async () => {
                                    // Estrai l'ID della società dal primo avviso che contiene "Società" e "non trovata"
                                    const societaWarning = matchingStatus.warnings.find(w => 
                                      w.message.includes('Società') && w.message.includes('non trovata')
                                    );
                                    
                                    if (societaWarning) {
                                      // Estrai l'ID dalla riga del file
                                      const rowData = parsedData[societaWarning.row - 2]; // -2 perché l'header è riga 1
                                      if (rowData && rowData['Società']) {
                                        const societaId = parseInt(rowData['Società']);
                                        if (!isNaN(societaId)) {
                                          await createMissingSocieta(societaId);
                                          // Ricarica l'analisi del file
                                          processUploadedFile();
                                        }
                                      }
                                    }
                                  }}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                >
                                  <PlusIcon className="h-4 w-4 mr-1" />
                                  Crea Società Mancante
                                </button>
                              </div>
                            )}
                            
                            {/* Pulsante per creare categoria mancante se ci sono avvisi di categoria non trovata */}
                            {matchingStatus.warnings.some(w => w.message.includes('Categoria') && w.message.includes('non trovata')) && (
                              <div className="mt-3">
                                <button
                                  onClick={async () => {
                                    // Estrai l'ID della categoria dal primo avviso che contiene "Categoria" e "non trovata"
                                    const categoriaWarning = matchingStatus.warnings.find(w => 
                                      w.message.includes('Categoria') && w.message.includes('non trovata')
                                    );
                                    
                                    if (categoriaWarning) {
                                      // Estrai l'ID dalla riga del file
                                      const rowData = parsedData[categoriaWarning.row - 2]; // -2 perché l'header è riga 1
                                      if (rowData && rowData['Categoria']) {
                                        const categoriaId = parseInt(rowData['Categoria']);
                                        if (!isNaN(categoriaId)) {
                                          await createMissingCategoria(categoriaId);
                                          // Ricarica l'analisi del file
                                          processUploadedFile();
                                        }
                                      }
                                    }
                                  }}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-800 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ml-2"
                                >
                                  <PlusIcon className="h-4 w-4 mr-1" />
                                  Crea Categoria Mancante
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Visualizza lo stato di importazione */}
                    {isImporting && (
                      <div className="my-6 p-6 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="text-lg font-medium text-blue-800 mb-3">Importazione in corso...</h4>
                        
                        <div className="mb-4">
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium text-blue-800">{importProgress}% completato</span>
                            <span className="text-sm font-medium text-blue-800">{importedCount} di {totalToImport} offerte</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div 
                              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                              style={{ width: `${importProgress}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="space-y-2 text-sm text-blue-800">
                          <p>• {importStatus}</p>
                          <p>• Analisi e controllo dati in corso</p>
                          <p>• Salvataggio nel database</p>
                          <p className="text-xs text-blue-600 mt-2 italic">Non chiudere questa finestra durante l'importazione</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Riepilogo importazione</h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Righe totali nel file</p>
                          <p className="text-lg font-medium">{parsedData.length}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Stato analisi</p>
                          <p className="text-lg font-medium">
                            {matchingStatus.ready ? (
                              <span className="text-green-600">Pronto per l'importazione</span>
                            ) : (
                              <span className="text-red-600">Ci sono errori da risolvere</span>
                            )}
                          </p>
                        </div>
                      </div>
                      
                      {/* Tabella di anteprima delle offerte da importare */}
                      {matchingStatus.vetture.length > 0 && (
                        <div className="mt-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Anteprima offerte ({matchingStatus.vetture.length})</h5>
                          <div className="overflow-x-auto max-h-64 border rounded">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID Vettura</th>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Società</th>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durata</th>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">KM</th>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Canone</th>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Anticipo</th>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disponibile</th>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {matchingStatus.vetture.slice(0, 5).map((offerta, idx) => (
                                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{offerta.idVettura}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{offerta.societa?.nome || 'N/D'}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{offerta.durata} mesi</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{offerta.kmAnnui.toLocaleString()}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">€{offerta.canone.toLocaleString('it-IT', {minimumFractionDigits: 2})}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">€{offerta.anticipo.toLocaleString('it-IT', {minimumFractionDigits: 2})}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${offerta.statoOfferta ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {offerta.statoOfferta ? 'Attiva' : 'Inattiva'}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                        {offerta.statoDisponibilita}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                                {matchingStatus.vetture.length > 5 && (
                                  <tr>
                                    <td colSpan="8" className="px-3 py-2 text-xs text-gray-500 text-center">
                                      ... e altre {matchingStatus.vetture.length - 5} offerte
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-end space-x-3 mt-4">
                      <button
                        onClick={() => setUploadStep(1)}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        disabled={isImporting}
                      >
                        Indietro
                      </button>
                      {!isImporting ? (
                        <button
                          onClick={() => saveOfferte()}
                          disabled={!matchingStatus.ready}
                          className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                            ${!matchingStatus.ready 
                              ? 'bg-gray-300 cursor-not-allowed' 
                              : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'}`}
                        >
                          Importa {matchingStatus.vetture.length} offerte
                        </button>
                      ) : (
                        <button
                          disabled
                          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-400 cursor-not-allowed"
                        >
                          Importazione in corso...
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modale per la gestione della scadenza */}
      {showScadenzaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
              <h3 className="text-lg font-medium text-gray-900">
                {scadenzaOfferte ? 'Modifica Scadenza Offerte' : 'Nuova Scadenza Offerte'}
              </h3>
              <button 
                onClick={() => setShowScadenzaModal(false)} 
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <label htmlFor="scadenzaOfferte" className="block text-sm font-medium text-gray-700 mb-1">
                  Data di Scadenza*
                </label>
                <input
                  type="date"
                  id="scadenzaOfferte"
                  value={editingScadenza.scadenzaOfferte}
                  onChange={(e) => setEditingScadenza({...editingScadenza, scadenzaOfferte: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  id="note"
                  value={editingScadenza.note || ''}
                  onChange={(e) => setEditingScadenza({...editingScadenza, note: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="px-4 py-3 bg-gray-50 flex justify-end space-x-2 rounded-b-lg">
              <button
                onClick={() => setShowScadenzaModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={saveScadenza}
                disabled={loadingScadenza}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {loadingScadenza ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Operazioni */}
      {showOperationsModal && (
        <div className="fixed z-[200] inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowOperationsModal(false)}></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Operazioni Database
                  </h3>
                  <button
                    onClick={() => {
                      setShowOperationsModal(false);
                      setTruncateConfirmStage(0);
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <button
                    onClick={() => {
                      setShowOperationsModal(false);
                      openConfigPage();
                    }}
                    className="w-full flex justify-between items-center px-4 py-3 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <div className="flex items-center">
                      <AdjustmentsHorizontalIcon className="h-5 w-5 text-gray-500 mr-3" />
                      <span className="text-gray-700">Gestione Campi</span>
                    </div>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowOperationsModal(false);
                      openUploadModal();
                    }}
                    className="w-full flex justify-between items-center px-4 py-3 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <div className="flex items-center">
                      <ArrowUpTrayIcon className="h-5 w-5 text-gray-500 mr-3" />
                      <span className="text-gray-700">Carica Offerte</span>
                    </div>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                  </button>
                  

                  
                  <button
                    onClick={() => {
                      setShowOperationsModal(false);
                      openOfferteEvidenzaModal();
                    }}
                    className="w-full flex justify-between items-center px-4 py-3 bg-white border border-yellow-300 rounded-md shadow-sm hover:bg-yellow-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  >
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-yellow-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="text-yellow-700 font-medium">Gestisci Offerte in Evidenza</span>
                    </div>
                    <ChevronRightIcon className="h-5 w-5 text-yellow-400" />
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowOperationsModal(false);
                      window.location.href = '/nlt/gestione-offerte-vetture/categorie';
                    }}
                    className="w-full flex justify-between items-center px-4 py-3 bg-white border border-blue-300 rounded-md shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7l2 2-2 2M5 13l2-2-2-2m0 6h14" />
                      </svg>
                      <span className="text-blue-700 font-medium">Gestione Categorie</span>
                    </div>
                    <ChevronRightIcon className="h-5 w-5 text-blue-400" />
                  </button>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-3">Operazioni Avanzate</h4>
                    
                    {truncateConfirmStage === 0 ? (
                      <button
                        onClick={truncateOfferteTable}
                        className="w-full flex justify-between items-center px-4 py-3 bg-white border border-red-300 rounded-md shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      >
                        <div className="flex items-center">
                          <TrashIcon className="h-5 w-5 text-red-500 mr-3" />
                          <span className="text-red-600 font-medium">Svuota Database Offerte</span>
                        </div>
                        <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
                      </button>
                    ) : truncateConfirmStage === 1 ? (
                      <div className="p-4 bg-red-50 rounded-md border border-red-300">
                        <p className="text-sm text-red-600 mb-3">
                          <ExclamationCircleIcon className="h-5 w-5 text-red-500 inline mr-2" />
                          Sei sicuro di voler svuotare completamente il database delle offerte? Questa azione non può essere annullata.
                        </p>
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => setTruncateConfirmStage(0)}
                            className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50"
                          >
                            Annulla
                          </button>
                          <button
                            onClick={truncateOfferteTable}
                            className="px-3 py-1.5 text-sm text-white bg-red-600 border border-transparent rounded shadow-sm hover:bg-red-700"
                          >
                            Sì, procedi
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-red-100 rounded-md border border-red-400">
                        <p className="text-sm text-red-700 font-bold mb-3">
                          <ExclamationCircleIcon className="h-5 w-5 text-red-600 inline mr-2" />
                          ATTENZIONE: Questa azione eliminerà TUTTE le offerte dal database e non potrà essere annullata!
                        </p>
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => setTruncateConfirmStage(0)}
                            className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50"
                          >
                            Annulla
                          </button>
                          <button
                            onClick={truncateOfferteTable}
                            disabled={truncateLoading}
                            className="px-3 py-1.5 text-sm text-white bg-red-600 border border-transparent rounded shadow-sm hover:bg-red-700 flex items-center"
                          >
                            {truncateLoading ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Elaborazione...
                              </>
                            ) : (
                              'Sì, sono sicuro'
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale per Offerte in Evidenza */}
      {showOfferteEvidenzaModal && (
        <div className="fixed z-[200] inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            
            {/* Modal */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full">
              {/* Header */}
              <div className="bg-white px-4 pt-4 pb-4 sm:p-6 sm:pb-4 border-b">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                    <svg className="h-6 w-6 text-yellow-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Gestione Offerte in Evidenza
                  </h3>
                  <button
                    onClick={() => setShowOfferteEvidenzaModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              {/* Body */}
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                {/* Filtro per sito vetrina */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contesto Offerte in Evidenza
                  </label>
                  <select
                    value={selectedSitoVetrina}
                    onChange={(e) => handleSitoVetrinaChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  >
                    <option value="globali">🌍 Offerte Globali (tutti i siti)</option>
                    {sitiVetrina.map(sito => (
                      <option key={sito.id} value={sito.id}>
                        🌐 {sito.nome} ({sito.dominio})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Filtri di ricerca */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Filtri di ricerca</h4>
                    <button
                      onClick={resetFiltersEvidenza}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Reimposta filtri
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* Ricerca testuale */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Ricerca vettura
                      </label>
                      <input
                        type="text"
                        value={searchOfferteEvidenza}
                        onChange={(e) => setSearchOfferteEvidenza(e.target.value)}
                        placeholder="Cerca marca, modello..."
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Filtro marca */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Marca
                      </label>
                      <select
                        value={filtroMarcaEvidenza}
                        onChange={(e) => setFiltroMarcaEvidenza(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Tutte le marche</option>
                        {getFilterOptions().marche.map(marca => (
                          <option key={marca} value={marca}>{marca}</option>
                        ))}
                      </select>
                    </div>

                    {/* Filtro durata */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Durata (mesi)
                      </label>
                      <select
                        value={filtroDurataEvidenza}
                        onChange={(e) => setFiltroDurataEvidenza(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Tutte le durate</option>
                        {getFilterOptions().durate.map(durata => (
                          <option key={durata} value={durata}>{durata} mesi</option>
                        ))}
                      </select>
                    </div>

                    {/* Filtro tipo cliente */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Tipo Cliente
                      </label>
                      <select
                        value={filtroTipoClienteEvidenza}
                        onChange={(e) => setFiltroTipoClienteEvidenza(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Tutti i tipi</option>
                        {getFilterOptions().tipiCliente.map(tipo => (
                          <option key={tipo} value={tipo}>{tipo}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Colonna sinistra: Offerte disponibili */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      Offerte Disponibili
                      <span className="ml-2 text-sm text-gray-500">
                        ({Object.keys(getFilteredOfferteGrouped()).length} vetture)
                      </span>
                    </h4>
                    
                    <div className="border border-gray-200 rounded-lg max-h-[500px] overflow-y-auto">
                      {loadingOfferteEvidenza ? (
                        <div className="p-4 text-center text-gray-500">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                          Caricamento...
                        </div>
                      ) : Object.keys(getFilteredOfferteGrouped()).length > 0 ? (
                        Object.entries(getFilteredOfferteGrouped()).map(([vetturaId, group]) => (
                          <div key={vetturaId} className="border-b border-gray-100 last:border-b-0">
                            {/* Header vettura */}
                            <div className="bg-gray-50 px-3 py-2 border-b border-gray-100">
                              <div className="flex items-center">
                                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                                <span className="text-sm font-medium text-gray-900">
                                  {group.vettura?.marca?.descrizione} {group.vettura?.modello}
                                </span>
                                {group.vettura?.allestimento && (
                                  <span className="ml-2 text-xs text-gray-600">
                                    {group.vettura.allestimento}
                                  </span>
                                )}
                                <span className="ml-auto text-xs text-gray-500">
                                  {group.offerte.length} offert{group.offerte.length === 1 ? 'a' : 'e'}
                                </span>
                              </div>
                            </div>
                            
                            {/* Offerte per questa vettura */}
                            {group.offerte.map(offerta => {
                              const isInEvidenza = offerteInEvidenza.some(oe => oe.idOfferta === offerta.id);
                              
                              return (
                                <div key={offerta.id} className={`p-3 ${isInEvidenza ? 'bg-yellow-50' : 'hover:bg-gray-50'}`}>
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="flex items-center mb-1">
                                        <span className="text-sm font-medium text-blue-600">
                                          €{offerta.canone}/mese
                                        </span>
                                        <span className="ml-2 text-xs text-gray-600">
                                          {offerta.durata} mesi • {offerta.kmAnnui.toLocaleString()} km/anno
                                        </span>
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {offerta.tipoCliente} • {offerta.societaNoleggio?.nome}
                                        {offerta.anticipo > 0 && ` • Anticipo €${offerta.anticipo}`}
                                      </div>
                                    </div>
                                    <div className="ml-3">
                                      {isInEvidenza ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-800">
                                          <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                          </svg>
                                          In Evidenza
                                        </span>
                                      ) : (
                                        <button
                                          onClick={() => addOffertaInEvidenza(offerta.id)}
                                          className="inline-flex items-center px-2 py-1 border border-transparent rounded-md text-xs font-medium text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                          <PlusIcon className="h-3 w-3 mr-1" />
                                          Aggiungi
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500">
                          {availableOfferte.length === 0 
                            ? 'Nessuna offerta disponibile'
                            : 'Nessuna offerta trovata con i filtri selezionati'
                          }
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Colonna destra: Offerte in evidenza */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      Offerte in Evidenza
                      <span className="ml-2 text-sm text-gray-500">
                        {selectedSitoVetrina === 'globali' ? '(Globali)' : `(${sitiVetrina.find(s => s.id == selectedSitoVetrina)?.nome || 'Sito Specifico'})`}
                      </span>
                    </h4>
                    
                    <div className="border border-gray-200 rounded-lg max-h-[500px] overflow-y-auto">
                      {loadingOfferteEvidenza ? (
                        <div className="p-4 text-center text-gray-500">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-2"></div>
                          Caricamento...
                        </div>
                      ) : offerteInEvidenza.length > 0 ? (
                        offerteInEvidenza.map(offertaEvidenza => (
                          <div key={offertaEvidenza.id} className="p-3 border-b border-gray-100 last:border-b-0 bg-yellow-50">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 mb-1">
                                  {offertaEvidenza.offerta?.vettura?.marca?.descrizione} {offertaEvidenza.offerta?.vettura?.modello}
                                  {offertaEvidenza.offerta?.vettura?.allestimento && ` - ${offertaEvidenza.offerta.vettura.allestimento}`}
                                </div>
                                <div className="flex items-center mb-1">
                                  <span className="text-sm font-medium text-blue-600">
                                    €{offertaEvidenza.offerta?.canone}/mese
                                  </span>
                                  <span className="ml-2 text-xs text-gray-600">
                                    {offertaEvidenza.offerta?.durata} mesi • {offertaEvidenza.offerta?.kmAnnui?.toLocaleString()} km/anno
                                  </span>
                                </div>
                                <div className="text-xs text-gray-500">
                                  {offertaEvidenza.offerta?.tipoCliente} • {offertaEvidenza.offerta?.societaNoleggio?.nome}
                                  {offertaEvidenza.offerta?.societaNoleggio?.id && (
                                    <span className="text-gray-400 ml-1">(ID: {offertaEvidenza.offerta.societaNoleggio.id})</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-400 mt-1">
                                  Aggiunta il: {new Date(offertaEvidenza.dataInserimento).toLocaleDateString('it-IT')}
                                </div>
                              </div>
                              <div className="ml-3">
                                <button
                                  onClick={() => removeOffertaInEvidenza(offertaEvidenza.id)}
                                  className="inline-flex items-center px-2 py-1 border border-transparent rounded-md text-xs font-medium text-red-600 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                                >
                                  <TrashIcon className="h-3 w-3 mr-1" />
                                  Rimuovi
                                </button>
                              </div>
                            </div>
                            
                            {/* Sezione per l'immagine personalizzata */}
                            <div className="border-t border-gray-200 pt-3">
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-medium text-gray-700">
                                  Immagine Personalizzata
                                </label>
                                {offertaEvidenza.immaginePersonalizzata && (
                                  <span className="text-xs text-green-600">✓ Caricata</span>
                                )}
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {/* Anteprima immagine se presente */}
                                {offertaEvidenza.immaginePersonalizzata && (
                                  <div className="w-12 h-12 border border-gray-300 rounded overflow-hidden bg-gray-100">
                                    <img
                                      src={offertaEvidenza.immaginePersonalizzata}
                                      alt="Anteprima"
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                )}
                                
                                {/* Input per caricamento immagine */}
                                <div className="flex-1">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleImmagineUpload(e, offertaEvidenza.id)}
                                    className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                  />
                                </div>
                              </div>
                              
                              <div className="text-xs text-gray-500 mt-1">
                                Formato: PNG, JPG (max 5MB)
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500">
                          {selectedSitoVetrina === 'globali' 
                            ? 'Nessuna offerta globale in evidenza' 
                            : 'Nessuna offerta in evidenza per questo sito'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="px-4 py-3 bg-gray-50 flex justify-between items-center rounded-b-lg">
                <div className="text-sm text-gray-500">
                  {offerteInEvidenza.length} offert{offerteInEvidenza.length === 1 ? 'a' : 'e'} in evidenza
                </div>
                <button
                  onClick={() => setShowOfferteEvidenzaModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferteVettureList; 